import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box,
  Card,
  CardContent,
  Collapse,
  Container,
  Fab,
  IconButton,
  Stack,
  TextField,
  Typography,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { 
  ArrowForward, 
  Save, 
  Cancel, 
  Schedule, 
  Add, 
  Remove,
  Delete,
  // ChangeCircle, 
  // ChangeCircleOutlined,
  ExpandMore,
  ExpandLess,
  CalendarToday,
  Close,
  Favorite,
  FavoriteBorder
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/it';
import { apiRequest } from '../api/api';
import { useStatsDipendente, useFestivita } from '../api/queries';
import { preferenzeService, cambiTurnoService, bloccoService, impostazioniGlobaliService } from '../api/services';
import CambiTurnoPopup from './CambiTurnoPopup';
import PreferenceLimitDialog from './PreferenceLimitDialog';
import MonthYearPicker from './MonthYearPicker';
import { CambiTurnoStats, TurnoCreato, Dipendente } from '../types/types';
import { isCompetenzaLocked, LOCK_MESSAGE } from '../utils/competenzaLock';
import { getApiErrorMessage } from '../utils/apiError';

// Configura dayjs per utilizzare la localizzazione italiana
dayjs.locale('it');

// Colore tema per Il Mio Orario
const PAGE_COLOR = '#2196F3';

// Converte un numero di ore (decimali) nel formato UI hh:ss
function formatHoursNumberToHHSS(value: number): string {
  if (value === null || value === undefined || isNaN(value as any)) return '0:00';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const hours = Math.floor(abs);
  let minutes = Math.round((abs - hours) * 60);
  if (minutes === 60) {
    return `${sign}${hours + 1}:00`;
  }
  const mm = minutes.toString().padStart(2, '0');
  return `${sign}${hours}:${mm}`;
}

// Opzioni per il tipo orario
// Opzioni ristrette per il dipendente (di base)
const TIPO_ORARIO_OPTIONS_RESTRICTED = [
  'LAVORATO',
  'ROL'
];

// Lista assenze che richiedono Turno = ASSENZA (Tipo Orario resta quello selezionato)
const TIPO_ASSENZA_LIST = [
  'CORSI AI DIPENDENTI',
  'CHIUSO DITTA',
  'ROL',
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'ALLATTAMENTO',
  'LUTTO',
  'PERMESSI PARENTALI',
  'CONGEDO MATRIMONIALE',
  'PERMESSI ELETTORALI',
  'ASPETTATIVA',
  'PERMESSI STUDIO',
  'CHIUSO ORDINANAZA',
  'QUARANTENA',
  'ASSENZA N.R. DL 127',
  'ASSENZA NON RETRIBUITA',
  'LEGGE 118 PERMESSO'
];

// Categoria A: mantiene preimpostazione orari (assenze a giornata intera, non permettono consuntivo)
const TIPO_ASSENZA_PREIMPOSTA_ORARI = [
  'FERIE',
  'MALATTIA',
  'MATERNITA\'',
  'CONGEDO MATRIMONIALE',
  'ASPETTATIVA'
];

// Codici che disabilitano completamente i campi consuntivo (non precompilati e non compilabili)
const CODICI_DISABILITA_CONSUNTIVO = [
  'ASSENZA NON RETRIBUITA'
];

// Assenze che permettono di inserire orari consuntivo (assenze parziali/permessi orari)
// CORSI AI DIPENDENTI e SMART WORKING: hanno orari previsti impostati dall'utente, permettono consolidamento
const ASSENZE_CON_CONSUNTIVO = [
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING'
];

// Codici con calcolo automatico (disabilita time) = Categoria A
// CHIUSO DITTA rimosso perché ora mostra le ore calcolate in base al contratto
// ASSENZA NON RETRIBUITA aggiunto per disabilitare completamente i campi consuntivo
const CODICI_CALCOLO_AUTOMATICO = [...TIPO_ASSENZA_PREIMPOSTA_ORARI, 'ROL', ...CODICI_DISABILITA_CONSUNTIVO];

const isAssenzaTipo = (t?: string | null) => !!t && TIPO_ASSENZA_LIST.includes(t);
const isCategoriaA = (t?: string | null) => !!t && TIPO_ASSENZA_PREIMPOSTA_ORARI.includes(t);
// Funzione helper per verificare se un codice richiede calcolo automatico
const isCodiceTipoOrarioSpeciale = (codiceTipoOrario: string | null | undefined): boolean => {
  return codiceTipoOrario ? CODICI_CALCOLO_AUTOMATICO.includes(codiceTipoOrario) : false;
};

// Funzione helper per verificare se un'assenza permette di inserire orari consuntivo
const isAssenzaConConsuntivo = (codiceTipoOrario: string | null | undefined): boolean => {
  return codiceTipoOrario ? ASSENZE_CON_CONSUNTIVO.includes(codiceTipoOrario) : false;
};

// Verifica se i campi consuntivo devono essere disabilitati
// Per le assenze con consuntivo (ROL, LEGGE 104, AVIS, ecc.) non disabilita
const isDisableConsuntivo = (codiceTipoOrario: string | null | undefined): boolean => {
  if (!codiceTipoOrario) return false;
  // ASSENZA NON RETRIBUITA: disabilita completamente i campi consuntivo
  if (CODICI_DISABILITA_CONSUNTIVO.includes(codiceTipoOrario)) return true;
  // Se è un'assenza che permette consuntivo, NON disabilitare
  if (isAssenzaConConsuntivo(codiceTipoOrario)) return false;
  // Altrimenti usa la logica standard
  return CODICI_CALCOLO_AUTOMATICO.includes(codiceTipoOrario);
};

// Ruoli che devono confermare gli orari in ordine cronologico
const RUOLI_CON_RESTRIZIONE_CONFERMA = ['dipendente', 'linker', 'maker', 'referente'];

// Verifica se il ruolo richiede la conferma sequenziale dei turni LAVORATO
const isRuoloConRestrizioneConferma = (ruolo: string): boolean => {
  return RUOLI_CON_RESTRIZIONE_CONFERMA.includes((ruolo || '').toLowerCase());
};

// Helper per gestire errori API e mostrare messaggi appropriati
const handleApiError = getApiErrorMessage;

// Tipi orario che richiedono consolidamento obbligatorio (oltre a LAVORATO):
// Categoria 2 = assenze parziali con calcolo ore rimanenti
// CORSI AI DIPENDENTI e SMART WORKING: hanno orari previsti impostati manualmente, richiedono consolidamento
const CODICI_OBBLIGO_CONSOLIDAMENTO = [
  'LAVORATO',
  'ROL',
  'LEGGE 104 PERMESSO',
  'AVIS',
  'LUTTO',
  'PERMESSI PARENTALI',
  'PERMESSI ELETTORALI',
  'PERMESSI STUDIO',
  'LEGGE 118 PERMESSO',
  'CORSI AI DIPENDENTI',
  'SMART WORKING',
];

// Verifica se esistono turni (LAVORATO o Categoria 2) precedenti non confermati nello stesso mese
// Ritorna true se ci sono turni precedenti non confermati (blocca l'inserimento)
const hasPreviousUnconfirmedLavorato = (
  orari: { id: number; data: string; oraConsInizio?: string | null; oraConsFine?: string | null; codiceTipoOrario?: string | null }[],
  currentTurnoData: string
): boolean => {
  const currentDate = dayjs(currentTurnoData);
  const currentMonth = currentDate.format('YYYY-MM');

  // Filtra i turni con obbligo di consolidamento dello stesso mese con data precedente
  const previousTurni = orari.filter(turno => {
    const turnoDate = dayjs(turno.data);
    const turnoMonth = turnoDate.format('YYYY-MM');

    return turnoMonth === currentMonth &&
           turnoDate.isBefore(currentDate, 'day') &&
           CODICI_OBBLIGO_CONSOLIDAMENTO.includes(turno.codiceTipoOrario || '') &&
           turno.id > 0; // Solo turni reali, non placeholder
  });

  // Verifica se almeno uno di questi turni non ha conferma (oraConsInizio o oraConsFine vuoti)
  return previousTurni.some(turno =>
    !turno.oraConsInizio || !turno.oraConsFine
  );
};

// Verifica se il dipendente può modificare/eliminare un turno in base al tipo orario
// Il dipendente può modificare solo turni con tipi che può inserire autonomamente
const canDipendenteModifyTipoOrario = (
  tipoOrario: string | null | undefined,
  permessiSpeciali: { hasSpecial: boolean; remainingGg?: number; tipo?: number } | null
): boolean => {
  if (!tipoOrario) return true; // Se non c'è tipo orario, può modificare
  // Tipi base sempre modificabili dal dipendente
  const allowedTypes = [...TIPO_ORARIO_OPTIONS_RESTRICTED]; // LAVORATO, ROL
  // Se ha permessi speciali tipo 1 (Legge 104) con giorni disponibili
  if (permessiSpeciali?.hasSpecial && permessiSpeciali.tipo === 1 && (permessiSpeciali.remainingGg ?? 0) > 0) {
    allowedTypes.push('LEGGE 104 PERMESSO');
  }
  return allowedTypes.includes(tipoOrario);
};

const isPreferenceTurno = (orario?: { preferenza?: boolean | number | null } | null): boolean => {
  return Number(orario?.preferenza || 0) === 1;
};

const getPreferencePayload = (response: any) => response?.data ?? response?.turno ?? response;

/**
 * Componente "Il Mio Orario" ottimizzato per dispositivi mobile
 * Layout a cards verticali, interfaccia touch-friendly
 */
const MioOrarioCel: React.FC = () => {
  useEffect(() => {
    document.documentElement.dataset.turnoSource = 'MIO_ORARIO_CEL';
    return () => { delete document.documentElement.dataset.turnoSource; };
  }, []);

  const [orari, setOrari] = useState<TurnoCreato[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [editingRows, setEditingRows] = useState<{[key: number]: {oraConsInizio: string, oraConsFine: string, tipoOrario: string, annotazioni: string}}>({});
  const [showActionsForRow, setShowActionsForRow] = useState<number | null>(null);
  // Rimosso stato non utilizzato (activeCell)

  // Stati mobile specifici
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [showCalendar, setShowCalendar] = useState(false);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

  // Stati per gestione preferenze
  const [isPreferencesPeriod, setIsPreferencesPeriod] = useState(false);
  // Manteniamo solo il setter per evitare warning su variabile non usata
  const [, setPreferenceCount] = useState(0);
  const [isTargetMonth, setIsTargetMonth] = useState(false);

  // Statistiche dipendente (mese selezionato) e info dipendente per ore contratto
  const [stats, setStats] = useState({
    oreContratto: 0,
    orePianificate: 0,
    oreConsuntivate: 0,
    assenzeContrattuali: 0,
    attuale: 0,
    differenzaAttuale: 0
  });
  const [dipInfo, setDipInfo] = useState<Dipendente | null>(null);

  // Stati per dialog selezione turno
  const [turnoDialogOpen, setTurnoDialogOpen] = useState(false);
  const [selectedOrario, setSelectedOrario] = useState<TurnoCreato | null>(null);
  const [dialogType, setDialogType] = useState<'insert' | 'modify' | 'delete' | 'updateRiposo'>('insert');
  const [preferenceSubmitting, setPreferenceSubmitting] = useState(false);
  
  // Stato per mantenere la posizione dello scroll
  const [pendingScrollPosition, setPendingScrollPosition] = useState<number | null>(null);
  const [scrollToId, setScrollToId] = useState<number | null>(null);
  const [openTipoOrarioId, setOpenTipoOrarioId] = useState<number | null>(null);

  // Stati per cambi turno
  const [cambiTurnoPopupOpen, setCambiTurnoPopupOpen] = useState(false);
  const [, setCambiTurnoStats] = useState<CambiTurnoStats | null>(null);

  // Date blocco applicabili per ruolo e DataControllo (per dipendente)
  const [dataBloccoGlobale, setDataBloccoGlobale] = useState<dayjs.Dayjs | null>(null);
  const [dataBloccoCoordinatori, setDataBloccoCoordinatori] = useState<dayjs.Dayjs | null>(null);
  const [dataControllo, setDataControllo] = useState<dayjs.Dayjs | null>(null);
  
  useEffect(() => {
    const loadBlocchi = async () => {
      try {
        const [dataGlobale, dataCoordinatori] = await Promise.all([
          impostazioniGlobaliService.getDataBloccoGlobale(),
          impostazioniGlobaliService.getDataBloccoCoordinatori()
        ]);
        if (dataGlobale) {
          setDataBloccoGlobale(dayjs(dataGlobale));
        } else {
          setDataBloccoGlobale(null);
        }
        setDataBloccoCoordinatori(dataCoordinatori ? dayjs(dataCoordinatori) : null);
        
        // Legge DataControllo per il dipendente corrente
        const b = await bloccoService.getMine();
        if (b && b.dataControllo) {
          setDataControllo(dayjs(b.dataControllo));
        } else {
          setDataControllo(null);
        }
      } catch (e) {
        setDataBloccoGlobale(null);
        setDataBloccoCoordinatori(null);
        setDataControllo(null);
      }
    };
    loadBlocchi();
  }, []);

  // La data globale si applica ai soli ruoli ordinari; il Coordinatore usa la propria data blocco.
  const ruoloCorrente = (JSON.parse(localStorage.getItem('userInfo') || '{}').ruolo || '').toLowerCase();
  const isAdmin = ruoloCorrente === 'admin';
  const dataBloccoRuolo = ruoloCorrente === 'coordinatore'
    ? dataBloccoCoordinatori
    : isAdmin ? null : dataBloccoGlobale;
  const dataBloccante = isAdmin ? null : (() => {
    if (dataBloccoRuolo && dataControllo) {
      return dataBloccoRuolo.isAfter(dataControllo, 'day') || dataBloccoRuolo.isSame(dataControllo, 'day')
        ? dataBloccoRuolo : dataControllo;
    }
    return dataBloccoRuolo || dataControllo || null;
  })();

  const isDateBlocked = (dateStr?: string | null) => {
    if (isAdmin || !dateStr || !dataBloccante) return false;
    const d = dayjs(dateStr);
    return !d.isAfter(dataBloccante, 'day'); // blocco se data <= dataBloccante
  };
  const isTurnoBlocked = (turno: TurnoCreato) =>
    isDateBlocked(turno.data) || (!isAdmin && Boolean(turno.bloccoAutomatico));
  // Impostazioni globali (dal backend)
  const [sliderAlwaysEnabled, setSliderAlwaysEnabled] = useState(false);
  useEffect(() => {
    const loadImpostazioneGlobale = async () => {
      try {
        const { impostazioniService } = await import('../api/impostazioniService');
        const impostazione = await impostazioniService.getTrascinaConfermareSempre();
        setSliderAlwaysEnabled(impostazione.enabled);
      } catch (error) {
        console.error('Errore nel caricamento dell\'impostazione globale:', error);
        // Fallback al localStorage per compatibilità
        try {
          const raw = localStorage.getItem('appSettings');
          if (raw) {
            const parsed = JSON.parse(raw);
            setSliderAlwaysEnabled(!!parsed.sliderAlwaysEnabled);
          }
        } catch {}
      }
    };
    
    loadImpostazioneGlobale();
  }, []);

  // Stato e caricamento permessi speciali per l'utente corrente (mobile)
  const [permessiSpeciali, setPermessiSpeciali] = useState<{ hasSpecial: boolean; remainingGg?: number; tipo?: number } | null>(null);
  useEffect(() => {
    const loadPermessi = async () => {
      try {
        const month = selectedDate.format('YYYY-MM');
        const data = await apiRequest<any>(`/permessi-speciali/mine?month=${month}`);
        if (data) {
          setPermessiSpeciali({ hasSpecial: !!data.hasSpecial, remainingGg: data.remainingGg, tipo: data.tipo });
        } else {
          setPermessiSpeciali({ hasSpecial: false });
        }
      } catch (e) {
        setPermessiSpeciali({ hasSpecial: false });
      }
    };
    loadPermessi();
  }, [selectedDate]);

  // Stato per tracciare se è stato fatto lo scroll iniziale al giorno corrente
  const initialScrollDoneRef = useRef(false);

  // Effetto per scrollare alla card del giorno corrente quando gli orari vengono caricati
  useEffect(() => {
    // Scroll solo se:
    // 1. Abbiamo orari caricati
    // 2. Non è già stato fatto lo scroll iniziale per questo mese
    // 3. Il mese selezionato è il mese corrente
    if (orari.length > 0 && !initialScrollDoneRef.current) {
      const today = dayjs();
      const isCurrentMonth = selectedDate.isSame(today, 'month');
      
      if (isCurrentMonth) {
        const todayStr = today.format('YYYY-MM-DD');
        const todayCard = orari.find(o => o.data === todayStr);
        
        if (todayCard) {
          // Piccolo delay per assicurarsi che il DOM sia pronto
          const timer = setTimeout(() => {
            const element = document.getElementById(`card-${todayCard.id}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              initialScrollDoneRef.current = true;
            }
          }, 300);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [orari, selectedDate]);

  // Reset dello scroll quando cambia il mese
  useEffect(() => {
    initialScrollDoneRef.current = false;
  }, [selectedDate.format('YYYY-MM')]);

  // Effetto per scrollare alla card appena creata
  useEffect(() => {
    if (scrollToId !== null) {
      // Timeout aumentato a 500ms per garantire che il DOM sia pronto
      const timer = setTimeout(() => {
        const element = document.getElementById(`card-${scrollToId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Reset scrollToId dopo lo scroll
          setTimeout(() => setScrollToId(null), 1000);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [scrollToId, orari]);

  // Dialog limite preferenze
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogWeekPrefs, setLimitDialogWeekPrefs] = useState<{ id: number; data: string; turno?: string }[]>([]);

  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  // Admin bypassa tutti i lock di competenza (mesi precedenti)
  const isAdminUser = (userInfo.ruolo || '').toLowerCase() === 'admin';
  const isLockedForUser = (year: number, month: number) => isAdminUser ? false : isCompetenzaLocked(year, month);

  // Preferenze: funzioni di periodo (allineate a MioOrario)
  const checkPreferencesPeriod = () => {
    // Nuova regola: dal 16 alla fine del mese corrente
    return dayjs().date() >= 16;
  };

  const calculateTargetMonth = () => {
    // Mese target = secondo mese successivo
    return dayjs().add(2, 'month').startOf('month');
  };

  const checkIfTargetMonth = (selectedMonth: Dayjs, targetMonth: Dayjs) => {
    // Solo se il mese selezionato coincide esattamente con il target
    return selectedMonth.isSame(targetMonth, 'month');
  };

  const monthStart = selectedDate.startOf('month').format('YYYY-MM-DD');
  const monthEnd = selectedDate.endOf('month').format('YYYY-MM-DD');
  const { data: festivitaData } = useFestivita(monthStart, monthEnd);

  // Funzioni helper mobile
  const toggleCardExpansion = (cardId: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
      // Se stiamo chiudendo la card, rimuoviamo lo stato di editing per questa riga
      setEditingRows(prev => {
        const newState = { ...prev };
        delete newState[cardId];
        return newState;
      });
      setShowActionsForRow(null);
    } else {
      newExpanded.add(cardId);
      // Quando si espande la card, avvia automaticamente la modalità editing
      const turno = orari.find(o => o.id === cardId);
      if (turno) {
        // Verifica se è possibile modificare il turno
        const role = (userInfo.ruolo || '').toLowerCase();
        const isDipendente = role === 'dipendente';
        const tipoOrarioTurno = turno.codiceTipoOrario || null;
        const canModifyThisTurno = !isDipendente || canDipendenteModifyTipoOrario(tipoOrarioTurno, permessiSpeciali);
        const blocked = isTurnoBlocked(turno);
        
        if (canModifyThisTurno && !blocked && !isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
          const formatTimeForInput = (time: string | undefined) => {
            if (!time) return '';
            return time.length > 5 ? time.substring(0, 5) : time;
          };
          setEditingRows(prev => ({
            ...prev,
            [cardId]: {
              oraConsInizio: formatTimeForInput(turno.oraConsInizio),
              oraConsFine: formatTimeForInput(turno.oraConsFine),
              tipoOrario: turno.codiceTipoOrario || '',
              annotazioni: turno.annotazioni || ''
            }
          }));
          setShowActionsForRow(cardId);
        }
      }
    }
    setExpandedCards(newExpanded);
  };

  const formatTimeRange = (start: string | undefined, end: string | undefined): string => {
    if (!start || !end) return 'Da Compilare';
    return `${start.substring(0, 5)} - ${end.substring(0, 5)}`;
  };

  // Rimosso helper isWeekend non utilizzato

  // Helper per verificare se un orario ha turno NULL/vuoto con codiceTipoOrario = RIPOSO
  // In questo caso deve essere visualizzato come "Disponibile" (senza turno assegnato)
  const isRiposoSenzaTurno = (orario: TurnoCreato): boolean => {
    const isTurnoNullOrEmpty = !orario.turno || orario.turno.trim() === '';
    const isCodiceTipoOrarioRiposo = orario.codiceTipoOrario === 'RIPOSO';
    return isTurnoNullOrEmpty && isCodiceTipoOrarioRiposo;
  };

  // Helper: verifica se è un giorno di Riposo (con turno assegnato o senza turno)
  // Questi record devono permettere l'inserimento di preferenze con cuoricino
  const isRiposoDay = (orario: TurnoCreato): boolean => {
    return orario.codiceTipoOrario === 'RIPOSO' || 
           (orario.turno || '').toLowerCase() === 'riposo';
  };

  // Helper per ottenere la label del turno da visualizzare
  const getTurnoDisplayLabel = (orario: TurnoCreato): string => {
    if (isRiposoSenzaTurno(orario)) {
      return 'Disponibile';
    }
    return orario.turno || 'Disponibile';
  };

  // Helper per ottenere il turno da usare per il colore
  const getTurnoForColor = (orario: TurnoCreato): string => {
    if (isRiposoSenzaTurno(orario)) {
      return ''; // Restituisce stringa vuota per usare il colore di default (grigio)
    }
    return orario.turno || '';
  };

  const getTurnoDisplayColor = (turno: string): string => {
    switch (turno) {
      case 'Mattina':
      case 'Mattina P.':
        return '#35abec';
      case 'Intermezzo':
      case 'Intermezzo P.':
        return '#60b964';
      case 'Pomeriggio':
      case 'Pomeriggio P.':
        return '#FDD65C';
      case 'Riposo':
      case 'RIPOSO': return '#d5d3d3';
      case 'ASSENZA':
      case 'Assenza': return '#ef5350'; // Rosso per assenze
      default: return '#757575';
    }
  };

  // Helper: negozio principale e negozio temporaneo per il turno
  const getMainStore = (): string => {
    const dipNeg = dipInfo?.neg;
    const uiNeg = (userInfo as any)?.neg || (userInfo as any)?.negozio;
    return (dipNeg || uiNeg || '').toString().trim().toUpperCase();
  };
  // Nuovo helper: mostra sempre il valore della colonna Neg del turno
  // Ma non mostrarlo per i record "RIPOSO senza turno" (che devono apparire come giorni vuoti)
  const getStoreCodeForTurno = (o: TurnoCreato): string | null => {
    // Se è un RIPOSO senza turno, non mostrare il negozio
    if (isRiposoSenzaTurno(o)) return null;
    const turnoNeg = (o.neg || '').toString().trim().toUpperCase();
    return turnoNeg ? turnoNeg : null;
  };

  // Colori per bottoni dialog preferenze (coerenti con desktop)
  const getTurnoColor = (turno: string) => {
    switch (turno) {
      case 'Mattina':
      case 'Mattina P.':
        return '#35abec';
      case 'Pomeriggio':
      case 'Pomeriggio P.':
        return '#FDD65C';
      case 'Intermezzo':
      case 'Intermezzo P.':
        return '#60b964';
      case 'Riposo':
      case 'RIPOSO': return '#d5d3d3';
      default: return '#e0e0e0';
    }
  };

  const getTurnoTextColor = (turno: string) => {
    return '#000000';
  };

  // Helper per mantenere l'ordine per data (ascendente)
  const sortOrariByDate = useCallback((list: TurnoCreato[]) => {
    try {
      // Ordina per data ascendente, e per lo stesso giorno ordina i turni
      // secondo l'ordine desiderato: Mattina, Intermezzo, Pomeriggio
      const turnoOrder: Record<string, number> = {
        'mattina': 0,
        'mattina p': 1,
        'intermezzo': 2,
        'intermezzo p': 3,
        'pomeriggio': 4,
        'pomeriggio p': 5,
        'riposo': 6,
        'disponibile': 7
      };

      const normalizeTurno = (raw: string) =>
        raw
          .toLowerCase()
          .replace(/\./g, '') // rimuovi punti es. "P."
          .replace(/\s+/g, ' ') // normalizza spazi
          .trim();

      return [...list].sort((a, b) => {
        const da = dayjs(a.data).valueOf();
        const db = dayjs(b.data).valueOf();
        if (da !== db) return da - db;

        // Stesso giorno: ordina per campo 'turno' (se presente), altrimenti per oraConsInizio
  const ta = normalizeTurno((a.turno || a.codiceTipoOrario || '').toString());
  const tb = normalizeTurno((b.turno || b.codiceTipoOrario || '').toString());

  const pa = Object.prototype.hasOwnProperty.call(turnoOrder, ta) ? turnoOrder[ta] : Number.MAX_SAFE_INTEGER;
  const pb = Object.prototype.hasOwnProperty.call(turnoOrder, tb) ? turnoOrder[tb] : Number.MAX_SAFE_INTEGER;

        if (pa !== pb) return pa - pb;

        // Se entrambi non sono riconosciuti o hanno lo stesso tipo, fallback ad ordinare per ora inizio consuntivo/previsto
        const timeA = (a.oraConsInizio || a.oraPrevInizio || '').toString();
        const timeB = (b.oraConsInizio || b.oraPrevInizio || '').toString();
        if (timeA && timeB) {
          return timeA.localeCompare(timeB);
        }

        return 0;
      });
    } catch {
      return list;
    }
  }, []);

  // Effect per ripristinare la posizione dello scroll
  useEffect(() => {
    if (pendingScrollPosition !== null) {
      // Usa requestAnimationFrame per assicurarsi che il DOM sia aggiornato
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.scrollTo({
            top: pendingScrollPosition,
            behavior: 'auto'
          });
          setPendingScrollPosition(null);
        }, 50); // Piccolo delay per assicurarsi che il rendering sia completo
      });
    }
  }, [orari, pendingScrollPosition]);

  // Formatta il tempo per la visualizzazione
  const formatTime = (time: string | undefined) => {
    if (!time) return '';
    return time.substring(0, 5); // Rimuove i secondi se presenti
  };

  // Formatta la data per la visualizzazione
  const formatDataDisplay = (data: string): string => {
    const date = dayjs(data);
    const dayName = date.format('dddd');
    const formattedDate = date.format('DD/MM/YYYY');
    const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    return `${capitalizedDayName} ${formattedDate}`;
  };

  // Determina automaticamente il tipo di turno basandosi sull'ora di inizio e codice tipo orario
  const determinaTipoTurno = (oraConsInizio: string | null, codiceTipoOrario: string | null, fallbackTurno?: string | null): string => {
    if (codiceTipoOrario === 'RIPOSO') {
      return 'Riposo';
    }

    if (isAssenzaTipo(codiceTipoOrario || undefined)) {
      return 'ASSENZA';
    }
    
    if (codiceTipoOrario && codiceTipoOrario !== 'LAVORATO') {
      return codiceTipoOrario;
    }
    
    if (codiceTipoOrario === 'LAVORATO' && oraConsInizio) {
      const [ore, minuti] = oraConsInizio.split(':').map(Number);
      const minutiTotali = ore * 60 + minuti;
      
      if (minutiTotali < 600) {
        return 'Mattina';
      } else if (minutiTotali >= 600 && minutiTotali < 810) {
        return 'Intermezzo';
      } else {
        return 'Pomeriggio';
      }
    }

    // Se le ore cons sono vuote ma c'è un turno esistente (derivato dall'ora prev), lo preserva
    if (fallbackTurno && fallbackTurno !== 'ASSENZA') {
      return fallbackTurno;
    }
    
    return '';
  };

  // Genera una griglia vuota solo dal primo all'ultimo giorno del mese selezionato
  const generateEmptyMonthGrid = (date: Dayjs): TurnoCreato[] => {
    const monthStart = date.startOf('month');
    const monthEnd = date.endOf('month');
    const grid: TurnoCreato[] = [];

    for (let day = monthStart; day.isBefore(monthEnd) || day.isSame(monthEnd, 'day'); day = day.add(1, 'day')) {
      grid.push({
        id: -parseInt(`${day.format('YYYYMMDD')}`),
        neg: userInfo.neg || '',
        descrizione: userInfo.nome || '',
        data: day.format('YYYY-MM-DD'),
        oraConsInizio: undefined,
        oraConsFine: undefined,
        oraPrevInizio: undefined,
        oraPrevFine: undefined,
        giornoSettimana: day.format('dddd'),
        turno: 'Disponibile',
        utenteModifica: userInfo.nome || '',
        dataModifica: new Date().toISOString(),
  blocco: 0,
  outOfMonth: false
      } as any);
    }
    
    return grid;
  };

  // Calcola il totale delle ore lavorate
  const calculateTotalHours = (startTime: string | undefined, endTime: string | undefined): string => {
    if (!startTime || !endTime) return '0:00';
    
    const start = dayjs(`2000-01-01 ${startTime}`);
    const end = dayjs(`2000-01-01 ${endTime}`);
    
    if (end.isBefore(start)) {
      const endNextDay = end.add(1, 'day');
      const diff = endNextDay.diff(start, 'minutes');
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    } else {
      const diff = end.diff(start, 'minutes');
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
  };

  // Avvia la modalità di modifica per una riga
  const startEditing = (turnoId: number, oraConsInizio: string | undefined, oraConsFine: string | undefined) => {
    const formatTimeForInput = (time: string | undefined) => {
      if (!time) return '';
      return time.length > 5 ? time.substring(0, 5) : time;
    };

    const turno = orari.find(o => o.id === turnoId);
    setEditingRows({
      ...editingRows,
      [turnoId]: {
        oraConsInizio: formatTimeForInput(oraConsInizio),
        oraConsFine: formatTimeForInput(oraConsFine),
        tipoOrario: turno?.codiceTipoOrario || '',
        annotazioni: turno?.annotazioni || ''
      }
    });
    setShowActionsForRow(turnoId);
  };

  // Gestisce il click su una cella per iniziare la modifica
  const handleCellClick = (rowId: number, field: 'oraConsInizio' | 'oraConsFine' | 'tipoOrario' | 'annotazioni', currentValue: string | undefined) => {
    // Blocca editing se periodo bloccato
    if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
      alert(LOCK_MESSAGE);
      return;
    }
    const turno = orari.find(o => o.id === rowId);
    if (!turno) return;
    // Blocca editing se la data è bloccata dal Coordinatore/Admin
    if (isTurnoBlocked(turno)) {
      alert(`Modifiche bloccate fino al ${dataBloccante?.format('YYYY-MM-DD')}`);
      return;
    }

    if (!editingRows[rowId]) {
      startEditing(rowId, turno.oraConsInizio, turno.oraConsFine);
    }
  };

  // Aggiorna il valore in modalità di modifica
  const updateEditingValue = (rowId: number, field: string, value: string) => {
    const updatedEditingRow = {
      ...editingRows[rowId],
      [field]: value
    };

    // Se viene selezionato un codice tipo orario speciale (Categoria A), azzera gli orari
    if (field === 'tipoOrario') {
      if (isCodiceTipoOrarioSpeciale(value)) {
        updatedEditingRow.oraConsInizio = '';
        updatedEditingRow.oraConsFine = '';
      }

      // LOGICA ASSENZE CON CONSUNTIVO (ROL, LEGGE 104, AVIS, ecc.): 
      // Se selezionato uno di questi codici, controlla se esiste un turno LAVORATO nello stesso giorno
      // e se le ore lavorate sono inferiori a quelle contrattuali, riempi automaticamente con la differenza
      const CODICI_AUTOCOMPILA_CONSUNTIVO = ['ROL', 'LEGGE 104 PERMESSO', 'AVIS', 'LUTTO', 'PERMESSI PARENTALI', 'PERMESSI ELETTORALI', 'PERMESSI STUDIO', 'LEGGE 118 PERMESSO'];
      if (CODICI_AUTOCOMPILA_CONSUNTIVO.includes(value) && dipInfo) {
        const currentOrario = orari.find(o => o.id === rowId);
        if (currentOrario) {
            // Ottieni ore contrattuali per il giorno
            const dayOfWeek = dayjs(currentOrario.data).day(); // 0=Domenica, 1=Lunedi...
            let contractHours = 0;
            switch(dayOfWeek) {
              case 1: contractHours = dipInfo.lunedi; break;
              case 2: contractHours = dipInfo.martedi; break;
              case 3: contractHours = dipInfo.mercoledi; break;
              case 4: contractHours = dipInfo.giovedi; break;
              case 5: contractHours = dipInfo.venerdi; break;
              case 6: contractHours = dipInfo.sabato; break;
              case 0: contractHours = dipInfo.domenica; break;
            }

            // DEFAULT SINGLE ROW: 00:00 -> 00:00 + contractHours
            if (contractHours > 0) {
                 const start = dayjs().hour(0).minute(0);
                 const end = start.add(contractHours, 'hour');
                 updatedEditingRow.oraConsInizio = start.format('HH:mm');
                 updatedEditingRow.oraConsFine = end.format('HH:mm');
            }

          // Cerca un altro turno LAVORATO nello stesso giorno
          const lavoratoShift = orari.find(o => 
            o.data === currentOrario.data && 
            o.id !== rowId && 
            (o.codiceTipoOrario === 'LAVORATO') &&
            o.oraConsInizio && o.oraConsFine
          );

          if (lavoratoShift && lavoratoShift.oraConsInizio && lavoratoShift.oraConsFine) {
            // Calcola ore lavorate
            const start = dayjs(`2000-01-01 ${lavoratoShift.oraConsInizio}`);
            const end = dayjs(`2000-01-01 ${lavoratoShift.oraConsFine}`);
            const workedMinutes = end.diff(start, 'minute');
            const workedHours = workedMinutes / 60;

            // Se ore lavorate < ore contrattuali, riempi la differenza
            if (contractHours > workedHours) {
              const remainingHours = contractHours - workedHours;
              // Nuovo Inizio = Fine del turno lavorato
              const newStart = end; 
              // Nuova Fine = Nuovo Inizio + ore mancanti
              const newEnd = newStart.add(remainingHours, 'hour');
              
              updatedEditingRow.oraConsInizio = newStart.format('HH:mm');
              updatedEditingRow.oraConsFine = newEnd.format('HH:mm');
            }
          }
        }
      }

      // LOGICA CHIUSO DITTA: Imposta le ore consuntive in base alle ore contrattuali
      if (value === 'CHIUSO DITTA' && dipInfo) {
        const currentOrario = orari.find(o => o.id === rowId);
        if (currentOrario) {
            // Ottieni ore contrattuali per il giorno
            const dayOfWeek = dayjs(currentOrario.data).day(); // 0=Domenica, 1=Lunedi...
            let contractHours = 0;
            switch(dayOfWeek) {
              case 1: contractHours = dipInfo.lunedi; break;
              case 2: contractHours = dipInfo.martedi; break;
              case 3: contractHours = dipInfo.mercoledi; break;
              case 4: contractHours = dipInfo.giovedi; break;
              case 5: contractHours = dipInfo.venerdi; break;
              case 6: contractHours = dipInfo.sabato; break;
              case 0: contractHours = dipInfo.domenica; break;
            }

            // Imposta 00:00 -> 00:00 + contractHours
            if (contractHours > 0) {
                 const start = dayjs().hour(0).minute(0);
                 const end = start.add(contractHours, 'hour');
                 updatedEditingRow.oraConsInizio = start.format('HH:mm');
                 updatedEditingRow.oraConsFine = end.format('HH:mm');
            } else {
                 // Se non ci sono ore contrattuali, imposta 00:00 - 00:00
                 updatedEditingRow.oraConsInizio = '00:00';
                 updatedEditingRow.oraConsFine = '00:00';
            }
        }
      }
    }

    setEditingRows({
      ...editingRows,
      [rowId]: updatedEditingRow
    });
  };

  // Copia l'orario previsto nel consuntivo e salva immediatamente
  const copyPrevToConsuntivo = async (turnoId: number, oraPrevInizio: string | undefined, oraPrevFine: string | undefined) => {
    // Blocca se periodo bloccato
    if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
      alert(LOCK_MESSAGE);
      return;
    }

    try {
      // Trova l'orario corrente per determinare il tipo orario automatico
      const orarioCorrente = orari.find(o => o.id === turnoId);
      if (orarioCorrente && isDateBlocked(orarioCorrente.data)) {
        alert(`Modifiche bloccate fino al ${dataBloccante?.format('YYYY-MM-DD')}`);
        return;
      }
      let tipoOrarioAutomatico = orarioCorrente?.codiceTipoOrario || '';
      
      // Codici assenza con consuntivo che richiedono calcolo automatico ore rimanenti (esclusi CORSI AI DIPENDENTI e SMART WORKING che copiano direttamente il previsto)
      const CODICI_AUTOCOMPILA_CONSUNTIVO = ['ROL', 'LEGGE 104 PERMESSO', 'AVIS', 'LUTTO', 'PERMESSI PARENTALI', 'PERMESSI ELETTORALI', 'PERMESSI STUDIO', 'LEGGE 118 PERMESSO'];
      
      // Non permettere la copia se è un codice speciale che NON supporta consuntivo
      // CORSI AI DIPENDENTI e SMART WORKING sono in ASSENZE_CON_CONSUNTIVO → copia diretta del previsto
      if (isCodiceTipoOrarioSpeciale(tipoOrarioAutomatico) && !isAssenzaConConsuntivo(tipoOrarioAutomatico)) {
        alert('Non è possibile copiare gli orari previsti per questo tipo di orario. Gli orari vengono calcolati automaticamente.');
        return;
      }
      
      // Se ci sono orari previsti e il tipo orario non è ancora impostato, imposta automaticamente "LAVORATO"
      if (!tipoOrarioAutomatico && oraPrevInizio && oraPrevFine) {
        tipoOrarioAutomatico = 'LAVORATO';
      }
      // Fallback: se il tipo orario non è ancora stato compilato e il turno è lavorativo, imposta "LAVORATO"
      else if (!tipoOrarioAutomatico && orarioCorrente?.turno) {
        const turnoLower = orarioCorrente.turno.toLowerCase();
        if (turnoLower.includes('mattina') || turnoLower.includes('pomeriggio') || turnoLower.includes('intermezzo')) {
          tipoOrarioAutomatico = 'LAVORATO';
        }
      }

      // Determina gli orari consuntivi da usare
      let oraConsInizioValue = oraPrevInizio ? oraPrevInizio.substring(0, 5) : '';
      let oraConsFineValue = oraPrevFine ? oraPrevFine.substring(0, 5) : '';

      // Per ASSENZE CON CONSUNTIVO, calcola le ore rimanenti
      if (CODICI_AUTOCOMPILA_CONSUNTIVO.includes(tipoOrarioAutomatico) && dipInfo && orarioCorrente) {
        // Ottieni ore contrattuali per il giorno
        const dayOfWeek = dayjs(orarioCorrente.data).day();
        let contractHours = 0;
        switch(dayOfWeek) {
          case 1: contractHours = dipInfo.lunedi; break;
          case 2: contractHours = dipInfo.martedi; break;
          case 3: contractHours = dipInfo.mercoledi; break;
          case 4: contractHours = dipInfo.giovedi; break;
          case 5: contractHours = dipInfo.venerdi; break;
          case 6: contractHours = dipInfo.sabato; break;
          case 0: contractHours = dipInfo.domenica; break;
        }

        // DEFAULT: Se non c'è LAVORATO, usa 00:00 -> ore contrattuali
        if (contractHours > 0) {
          const start = dayjs().hour(0).minute(0);
          const end = start.add(contractHours, 'hour');
          oraConsInizioValue = start.format('HH:mm');
          oraConsFineValue = end.format('HH:mm');
        }

        // Cerca un turno LAVORATO nello stesso giorno
        const lavoratoShift = orari.find(o => 
          o.data === orarioCorrente.data && 
          o.id !== turnoId && 
          (o.codiceTipoOrario === 'LAVORATO') &&
          o.oraConsInizio && o.oraConsFine
        );

        if (lavoratoShift && lavoratoShift.oraConsInizio && lavoratoShift.oraConsFine) {
          // Calcola ore lavorate
          const start = dayjs(`2000-01-01 ${lavoratoShift.oraConsInizio}`);
          const end = dayjs(`2000-01-01 ${lavoratoShift.oraConsFine}`);
          const workedMinutes = end.diff(start, 'minute');
          const workedHours = workedMinutes / 60;

          // Se ore lavorate < ore contrattuali, riempi la differenza
          if (contractHours > workedHours) {
            const remainingHours = contractHours - workedHours;
            const newStart = end;
            const newEnd = newStart.add(remainingHours, 'hour');
            
            oraConsInizioValue = newStart.format('HH:mm');
            oraConsFineValue = newEnd.format('HH:mm');
          }
        }
      }

      // Se non abbiamo orari validi e non è un'assenza con consuntivo, esci
      if (!oraConsInizioValue || !oraConsFineValue) {
        if (!CODICI_AUTOCOMPILA_CONSUNTIVO.includes(tipoOrarioAutomatico) && !isAssenzaConConsuntivo(tipoOrarioAutomatico)) {
          return;
        }
      }

      // Prepara i dati per il salvataggio
      const editData = {
        oraConsInizio: oraConsInizioValue,
        oraConsFine: oraConsFineValue,
        tipoOrario: tipoOrarioAutomatico,
        annotazioni: orarioCorrente?.annotazioni || ''
      };

      // Validazione
      const validationError = validateTimes(editData.oraConsInizio, editData.oraConsFine, editData.tipoOrario);
      if (validationError) {
        alert(validationError);
        return;
      }

  if (turnoId > 0) {
        // UPDATE: Record esistente nel database
        const params = new URLSearchParams();
        
        params.append('oraConsInizio', editData.oraConsInizio);
        params.append('oraConsFine', editData.oraConsFine);
        params.append('codiceTipoOrario', editData.tipoOrario);
        if (editData.annotazioni) params.append('annotazioni', editData.annotazioni);
        
        // Calcola automaticamente il turno
        const tipoTurnoAutomatico = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario);
        params.append('turno', tipoTurnoAutomatico);

        await apiRequest(`/orari-generati/${turnoId}/consuntivo?${params}`, {
          method: 'PUT'
        });
      } else {
        // CREATE: Nuova riga fittizia, crea un nuovo record
        const orario = orari.find(o => o.id === turnoId);
        if (!orario) {
          alert('Errore: impossibile trovare i dati della riga');
          return;
        }
        if (isTurnoBlocked(orario)) {
          alert(`Modifiche bloccate fino al ${dataBloccante?.format('YYYY-MM-DD')}`);
          return;
        }

        // Determina automaticamente il tipo di turno
        const tipoTurnoAutomatico = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario);

        const newTurnoData = {
          neg: userInfo.negozio || '',
          descrizione: userInfo.nome || '',
          data: orario.data,
          oraConsInizio: editData.oraConsInizio,
          oraConsFine: editData.oraConsFine,
          oraPrevInizio: null,
          oraPrevFine: null,
          giornoSettimana: orario.giornoSettimana,
          turno: tipoTurnoAutomatico,
          utenteModifica: userInfo.nome || '',
          codiceTipoOrario: editData.tipoOrario,
          annotazioni: editData.annotazioni || null
        };

        await apiRequest('/orari-generati', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newTurnoData)
        });
      }

      // AGGIORNAMENTO PARZIALE: Aggiorna il record salvato direttamente nello stato locale
      if (turnoId > 0) {
        // UPDATE case: aggiorna il record esistente
        setOrari(prevOrari => 
          prevOrari.map(orario => 
            orario.id === turnoId 
              ? {
                  ...orario,
                  oraConsInizio: editData.tipoOrario === 'RIPOSO' ? undefined : editData.oraConsInizio,
                  oraConsFine: editData.tipoOrario === 'RIPOSO' ? undefined : editData.oraConsFine,
                  codiceTipoOrario: editData.tipoOrario,
                  tipoOrario: editData.tipoOrario, // Fix: aggiorna anche tipoOrario per l'interfaccia
                  annotazioni: editData.annotazioni,
                  turno: determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario)
                } as TurnoCreato
              : orario
          )
        );
      } else {
        // CREATE case: aggiorna la riga fittizia
        const newOrario = {
          ...orari.find(o => o.id === turnoId)!,
          id: Date.now(),
          oraConsInizio: editData.oraConsInizio,
          oraConsFine: editData.oraConsFine,
          codiceTipoOrario: editData.tipoOrario,
          tipoOrario: editData.tipoOrario, // Fix: aggiorna anche tipoOrario per l'interfaccia
          annotazioni: editData.annotazioni,
          turno: determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario)
        } as TurnoCreato;

        setOrari(prevOrari => 
          prevOrari.map(o => 
            o.id === turnoId ? newOrario : o
          )
        );
      }
      
    } catch (err: any) {
      console.error('Error copying and saving schedule:', err);
      alert(handleApiError(err, 'Errore nel copiare e salvare l\'orario'));
    }
  };

  // Funzione di validazione (copiata da MioOrario.tsx)
  const validateTimes = (oraInizio: string, oraFine: string, tipoOrario?: string): string | null => {
    // Se il tipo orario è RIPOSO, non servono gli orari
    if (tipoOrario === 'RIPOSO') {
      return null;
    }
    
    // Se il tipo orario è uno speciale (es. FERIE, ROL, ecc.), gli orari potrebbero essere auto-calcolati
    // Permettiamo il salvataggio anche senza orari per questi tipi
    if (tipoOrario && isCodiceTipoOrarioSpeciale(tipoOrario)) {
      return null;
    }
    
    // Per LAVORATO: permettiamo il salvataggio anche senza ore consuntivo
    // Il tipoOrario è obbligatorio, ma oraInizio e oraFine possono essere vuoti
    if (!tipoOrario) {
      return 'Compilare il campo Tipo Orario';
    }
    
    // Se gli orari sono parzialmente compilati (uno sì e uno no), errore
    if ((oraInizio && !oraFine) || (!oraInizio && oraFine)) {
      return 'Compilare entrambi gli orari (Inizio e Fine) oppure lasciarli entrambi vuoti';
    }
    
    if (oraInizio && oraFine) {
      const startTime = dayjs(`2000-01-01 ${oraInizio}`);
      const endTime = dayjs(`2000-01-01 ${oraFine}`);
      
      if (endTime.isBefore(startTime)) {
        return "L'orario di uscita non può essere precedente all'orario di entrata";
      }
    }
    
    return null;
  };

  // Salva le modifiche del consuntivo (copiato da MioOrario.tsx)
  const saveConsuntivo = async (turnoId: number) => {
    // Blocca se periodo bloccato
    if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
      alert(LOCK_MESSAGE);
      return;
    }
    // Blocca se data <= DataBlocco
    const row = orari.find(o => o.id === turnoId);
    if (row && isTurnoBlocked(row)) {
      alert(`Modifiche bloccate fino al ${dataBloccante?.format('YYYY-MM-DD')}`);
      return;
    }
    try {
      const editData = editingRows[turnoId];
      if (!editData) return;

      // Validazione
  const validationError = validateTimes(editData.oraConsInizio, editData.oraConsFine, editData.tipoOrario);
      if (validationError) {
        alert(validationError);
        return;
      }

  if (turnoId > 0) {
        // UPDATE: Record esistente nel database
        const params = new URLSearchParams();
        
        // Se il tipo orario è RIPOSO, cancella gli orari
        if (editData.tipoOrario === 'RIPOSO') {
          params.append('oraConsInizio', ''); // Cancella
          params.append('oraConsFine', ''); // Cancella
        } else {
          // Altrimenti gestisci normalmente
          if (editData.oraConsInizio) params.append('oraConsInizio', editData.oraConsInizio);
          if (editData.oraConsFine) params.append('oraConsFine', editData.oraConsFine);
        }
        
        if (editData.tipoOrario) params.append('codiceTipoOrario', editData.tipoOrario);
        if (editData.annotazioni) params.append('annotazioni', editData.annotazioni);
        
  // Calcola automaticamente il turno anche per gli UPDATE
  // Se le ore cons sono vuote, preserva il turno corrente (derivato dall'ora prev)
  const orarioCorrenteCel = orari.find(o => o.id === turnoId);
  const tipoTurnoAutomatico = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario, orarioCorrenteCel?.turno);
        params.append('turno', tipoTurnoAutomatico);

        const updatedTurno = await apiRequest(`/orari-generati/${turnoId}/consuntivo?${params}`, {
          method: 'PUT'
        });
  setOrari(prevOrari => 
          prevOrari.map(orario => 
            orario.id === turnoId 
              ? {
                  ...orario,
                  oraConsInizio: updatedTurno.oraConsInizio ? 
                    (updatedTurno.oraConsInizio.length > 5 ? updatedTurno.oraConsInizio.substring(0, 5) : updatedTurno.oraConsInizio) 
                    : undefined,
                  oraConsFine: updatedTurno.oraConsFine ? 
                    (updatedTurno.oraConsFine.length > 5 ? updatedTurno.oraConsFine.substring(0, 5) : updatedTurno.oraConsFine) 
                    : undefined,
      // preserva il Tipo Orario selezionato lato client, non sostituire con 'ASSENZA'
      codiceTipoOrario: updatedTurno.codiceTipoOrario && updatedTurno.codiceTipoOrario !== 'ASSENZA' ? updatedTurno.codiceTipoOrario : editData.tipoOrario,
      tipoOrario: updatedTurno.codiceTipoOrario && updatedTurno.codiceTipoOrario !== 'ASSENZA' ? updatedTurno.codiceTipoOrario : editData.tipoOrario,
                  annotazioni: updatedTurno.annotazioni,
                  turno: updatedTurno.turno
                } as TurnoCreato
              : orario
          )
        );
  } else {
        // CREATE: Nuova riga fittizia, crea un nuovo record
        const orario = orari.find(o => o.id === turnoId);
        if (!orario) {
          alert('Errore: impossibile trovare i dati della riga');
          return;
        }
        if (isTurnoBlocked(orario)) {
          alert(`Modifiche bloccate fino al ${dataBloccante?.format('YYYY-MM-DD')}`);
          return;
        }

        // Determina automaticamente il tipo di turno
        const tipoTurnoAutomatico = determinaTipoTurno(editData.oraConsInizio, editData.tipoOrario);

        const newTurnoData = {
          neg: userInfo.negozio || '',
          descrizione: userInfo.nome || '',
          data: orario.data,
          // Se RIPOSO, cancella gli orari, altrimenti usa i valori inseriti
          oraConsInizio: editData.tipoOrario === 'RIPOSO' ? null : (editData.oraConsInizio || null),
          oraConsFine: editData.tipoOrario === 'RIPOSO' ? null : (editData.oraConsFine || null),
          oraPrevInizio: null,
          oraPrevFine: null,
          giornoSettimana: orario.giornoSettimana,
          turno: tipoTurnoAutomatico,
          utenteModifica: userInfo.nome || '',
          codiceTipoOrario: editData.tipoOrario || null,
          annotazioni: editData.annotazioni || null
        };

        const created = await apiRequest<TurnoCreato>('/orari-generati', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newTurnoData)
        });

        const serverTipoCreated = (created as any).tipoOrario || created.codiceTipoOrario;
        const safeTipoOrarioCreated = serverTipoCreated && serverTipoCreated !== 'ASSENZA'
          ? serverTipoCreated
          : editData.tipoOrario;

        // Usa la risposta del server: contiene anche il previsto calcolato e l'id reale.
        const newOrario = {
          ...orario,
          id: created.id,
          oraPrevInizio: created.oraPrevInizio ? created.oraPrevInizio.substring(0, 5) : undefined,
          oraPrevFine: created.oraPrevFine ? created.oraPrevFine.substring(0, 5) : undefined,
          oraConsInizio: created.oraConsInizio ? created.oraConsInizio.substring(0, 5) : undefined,
          oraConsFine: created.oraConsFine ? created.oraConsFine.substring(0, 5) : undefined,
          codiceTipoOrario: safeTipoOrarioCreated,
          tipoOrario: safeTipoOrarioCreated,
          annotazioni: created.annotazioni || undefined,
          turno: created.turno || tipoTurnoAutomatico
        } as TurnoCreato;

        setOrari(prevOrari => 
          prevOrari.map(o => 
            o.id === turnoId ? newOrario : o
          )
        );
      }
      
      // Chiudi la card e pulisci lo stato di editing dopo il salvataggio
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(turnoId);
        return newSet;
      });
      const newEditingRows = { ...editingRows };
      delete newEditingRows[turnoId];
      setEditingRows(newEditingRows);
      setShowActionsForRow(null);
      
      // Aggiorna le statistiche dopo il salvataggio
      refetchStats();
      
    } catch (err: any) {
      console.error('Error saving consuntivo:', err);
      alert(handleApiError(err, 'Errore nel salvataggio'));
    }
  };

  // Funzione per cancellare l'editing (copiata da MioOrario.tsx)
  const cancelEditing = (turnoId: number) => {
    const newEditingRows = { ...editingRows };
    delete newEditingRows[turnoId];
    setEditingRows(newEditingRows);
    setShowActionsForRow(null);
    // Chiudi anche la card
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(turnoId);
      return newSet;
    });
  };

  // Nuova funzione per cancellare i dati consuntivi (logica aggiornata)
  const clearConsuntivo = async (turnoId: number) => {
    // Blocca se periodo bloccato
    if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
      alert(LOCK_MESSAGE);
      return;
    }
    try {
      // setLoading(true); // Rimosso per evitare refresh intera pagina

      const orarioCorrente = orari.find(o => o.id === turnoId);
  if (orarioCorrente && !orarioCorrente.oraPrevInizio && !orarioCorrente.oraPrevFine) {
        // Nessun orario previsto: elimina il record backend MA lascia un placeholder per il giorno
        await apiRequest(`/orari-generati/${turnoId}`, { method: 'DELETE' });
    setOrari(prev => prev.map(o => {
          if (o.id === turnoId) {
      const placeholderId = -parseInt(dayjs(o.data).format('YYYYMMDD'));
            return {
              id: placeholderId,
              neg: (JSON.parse(localStorage.getItem('userInfo') || '{}').negozio) || '',
              descrizione: (JSON.parse(localStorage.getItem('userInfo') || '{}').nome) || '',
              data: o.data,
              oraConsInizio: undefined,
              oraConsFine: undefined,
              oraPrevInizio: undefined,
              oraPrevFine: undefined,
              giornoSettimana: o.giornoSettimana || dayjs(o.data).format('dddd'),
              turno: 'Disponibile',
              utenteModifica: '',
              dataModifica: '',
              blocco: 0,
              outOfMonth: o.outOfMonth
            } as any;
          }
          return o;
        }));
      } else {
        // Orari previsti presenti: cancella solo consuntivo + annotazioni + codice tipo orario
  const params = new URLSearchParams();
  params.append('oraConsInizio', '');
  params.append('oraConsFine', '');
  params.append('annotazioni', '');

  // Se il turno attuale è ROL, ripristina turno e codiceTipoOrario a LAVORATO
  if (orarioCorrente?.turno === 'ROL' || orarioCorrente?.codiceTipoOrario === 'ROL') {
    params.append('turno', 'LAVORATO');
    params.append('codiceTipoOrario', 'LAVORATO');
  }

  if (isLocked) throw new Error(LOCK_MESSAGE);
  await apiRequest(`/orari-generati/${turnoId}/consuntivo?${params}`, { method: 'PUT' });

        setOrari(prevOrari => prevOrari.map(orario => (
          orario.id === turnoId
            ? ({
                ...orario,
                oraConsInizio: null,
                oraConsFine: null,
                // Se era ROL, ripristina anche codiceTipoOrario
                codiceTipoOrario: (orario.turno === 'ROL' || orario.codiceTipoOrario === 'ROL') ? 'LAVORATO' : orario.codiceTipoOrario,
                annotazioni: undefined,
                turno: (orario.turno === 'ROL' || orario.codiceTipoOrario === 'ROL') ? 'LAVORATO' : orario.turno
              } as unknown as TurnoCreato)
            : orario
        )));
      }

      // Esci dalla modalità editing e chiudi la card
      const newEditingRows = { ...editingRows };
      delete newEditingRows[turnoId];
      setEditingRows(newEditingRows);
      setShowActionsForRow(null);
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(turnoId);
        return newSet;
      });
      
      // Aggiorna le statistiche dopo la cancellazione
      refetchStats();
    } catch (err: any) {
      console.error('Error clearing consuntivo:', err);
      alert(handleApiError(err, 'Errore nella cancellazione'));
    } finally {
      // setLoading(false);
    }
  };

  // Cancella ore consuntivo e resetta tipo orario a LAVORATO, turno basato sugli orari previsti
  const clearConsuntivoAndResetToLavorato = async (turnoId: number) => {
    // Blocca se periodo bloccato
    if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
      alert(LOCK_MESSAGE);
      return;
    }
    
    const confirmDelete = window.confirm(
      'Vuoi cancellare le ore consuntivo e reimpostare il tipo orario su LAVORATO?'
    );
    if (!confirmDelete) return;
    
    try {
      // Trova l'orario corrente per ottenere oraPrevInizio
      const orarioCorrente = orari.find(o => o.id === turnoId);
      
      // Determina il turno basato su oraPrevInizio
      // Mattina: <= 09:44, Intermezzo: 09:45 - 12:59, Pomeriggio: >= 13:00
      let turnoCalcolato = 'Mattina'; // default
      if (orarioCorrente?.oraPrevInizio) {
        const hhmm = orarioCorrente.oraPrevInizio.substring(0, 5);
        const [hhStr, mmStr] = hhmm.split(':');
        const hh = parseInt(hhStr, 10);
        const mm = parseInt(mmStr || '0', 10);
        if (!isNaN(hh) && !isNaN(mm)) {
          const minutes = hh * 60 + mm;
          if (minutes <= (9 * 60 + 44)) {
            turnoCalcolato = 'Mattina';
          } else if (minutes >= (9 * 60 + 45) && minutes <= (12 * 60 + 59)) {
            turnoCalcolato = 'Intermezzo';
          } else if (minutes >= 13 * 60) {
            turnoCalcolato = 'Pomeriggio';
          }
        }
      }
      
      const params = new URLSearchParams();
      params.append('oraConsInizio', '');
      params.append('oraConsFine', '');
      params.append('turno', turnoCalcolato);
      params.append('codiceTipoOrario', 'LAVORATO');

      await apiRequest(`/orari-generati/${turnoId}/consuntivo?${params}`, { method: 'PUT' });

      setOrari(prevOrari => prevOrari.map(orario => (
        orario.id === turnoId
          ? ({
              ...orario,
              oraConsInizio: null,
              oraConsFine: null,
              codiceTipoOrario: 'LAVORATO',
              turno: turnoCalcolato
            } as unknown as TurnoCreato)
          : orario
      )));

      // Esci dalla modalità editing e chiudi la card
      const newEditingRows = { ...editingRows };
      delete newEditingRows[turnoId];
      setEditingRows(newEditingRows);
      setShowActionsForRow(null);
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(turnoId);
        return newSet;
      });
      
      // Aggiorna le statistiche dopo il reset
      refetchStats();
    } catch (err: any) {
      console.error('Error clearing consuntivo and resetting to LAVORATO:', err);
      alert(handleApiError(err, 'Errore nella cancellazione'));
    }
  };

  // Gestisce annullamento o cancellazione turno (coerente con desktop)
  const handleCancelOrClear = async (turnoId: number, orario: TurnoCreato) => {
    // Caso speciale: se è una riga duplicata (__RIGA_DUPLICATA__), "Cancella il turno" deve cancellare la riga aggiunta
    if (orario.annotazioni === '__RIGA_DUPLICATA__') {
      const confirmDel = window.confirm('Vuoi cancellare definitivamente questa riga aggiunta?');
      if (confirmDel) {
        await deleteRow(orario);
      } else {
        cancelEditing(turnoId);
      }
      return;
    }

    const hasRealData = orario.oraConsInizio || orario.oraConsFine || orario.codiceTipoOrario || 
                       (orario.annotazioni && orario.annotazioni !== '__RIGA_DUPLICATA__');
    if (hasRealData) {
      const confirmDelete = window.confirm(
        'Vuoi cancellare definitivamente gli orari consuntivi inseriti?\n\n' +
        'ATTENZIONE: Questa azione cancellerà i dati salvati.'
      );
      if (confirmDelete) {
        await clearConsuntivo(turnoId);
      } else {
        cancelEditing(turnoId);
      }
    } else {
      cancelEditing(turnoId);
    }
  };

  // Duplica riga (coerente con desktop)
  const duplicateRow = async (e: React.MouseEvent, originalOrario: TurnoCreato) => {
    e.preventDefault();
    try {
      // Blocca se periodo bloccato
      if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
        alert(LOCK_MESSAGE);
        return;
      }
      const newTurno = {
        neg: originalOrario.neg,
        descrizione: originalOrario.descrizione,
        data: originalOrario.data,
        oraPrevInizio: null,
        oraPrevFine: null,
        giornoSettimana: originalOrario.giornoSettimana,
        turno: '',
        codiceTipoOrario: 'LAVORATO',
        annotazioni: '__RIGA_DUPLICATA__',
        utenteModifica: (JSON.parse(localStorage.getItem('userInfo') || '{}').nome) || 'Sistema'
      };
      const created = await apiRequest<TurnoCreato>('/orari-generati', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTurno)
      });

      // Aggiorna subito la UI inserendo la riga duplicata adiacente
      setOrari(prev => {
        const normalized: TurnoCreato = {
          ...(created as any),
          // Normalizza formato HH:mm e null->undefined per coerenza mobile
          oraConsInizio: created?.oraConsInizio ? created.oraConsInizio.substring(0, 5) : undefined,
          oraConsFine: created?.oraConsFine ? created.oraConsFine.substring(0, 5) : undefined,
          giornoSettimana: created?.giornoSettimana || originalOrario.giornoSettimana || dayjs(created?.data || originalOrario.data).format('dddd'),
          outOfMonth: (originalOrario as any).outOfMonth ?? false,
          codiceTipoOrario: created?.codiceTipoOrario || 'LAVORATO',
          annotazioni: created?.annotazioni || '__RIGA_DUPLICATA__'
        } as TurnoCreato;
        const idx = prev.findIndex(o => o.id === originalOrario.id);
        const next = idx === -1 ? [...prev, normalized] : [...prev.slice(0, idx + 1), normalized, ...prev.slice(idx + 1)];
        return sortOrariByDate(next);
      });

      // Espandi la nuova card, chiudi la card originale e prepara lo scroll
      if (created && created.id) {
        setExpandedCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(originalOrario.id); // Chiudi la card originale
          newSet.add(created.id); // Espandi la nuova card
          return newSet;
        });
        // Imposta lo scroll sulla nuova card
        setScrollToId(created.id);
      }

      // Rimosso fetchOrari per evitare reload completo della pagina
      // L'aggiornamento ottimistico sopra è sufficiente
    } catch (err: any) {
      console.error('Error duplicating row:', err);
      alert(handleApiError(err, 'Errore nella duplicazione della riga'));
    }
  };

  // Elimina una riga
  const deleteRow = async (orario: TurnoCreato) => {
    try {
      // Blocca se periodo bloccato
      if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
        alert(LOCK_MESSAGE);
        return;
      }
      // Esegui DELETE e aggiorna subito lo stato localmente (optimistic update)
      await apiRequest(`/orari-generati/${orario.id}`, { method: 'DELETE' });
      setOrari(prev => prev.filter(o => o.id !== orario.id));
      
      // Chiudi la card e pulisci lo stato
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(orario.id);
        return newSet;
      });
      
      // Rimosso fetchOrari per evitare reload completo della pagina (richiesto reload parziale)
    } catch (err: any) {
      console.error('Error deleting row:', err);
      alert(handleApiError(err, 'Errore nella cancellazione della riga'));
    }
  };

  // Carica gli orari dal server
  const fetchOrari = async (options?: { silent?: boolean }) => {
  if (!options?.silent) {
    setLoading(true);
  }
    setError(null);
    
    try {
  // Stato periodo preferenze
  const isPref = checkPreferencesPeriod();
  const targetM = calculateTargetMonth();
  const isTarget = checkIfTargetMonth(selectedDate, targetM);
  setIsPreferencesPeriod(isPref);
  setIsTargetMonth(isTarget);
  // Intervallo del mese corrente
  const meseInizio = selectedDate.startOf('month');
  const meseFine = selectedDate.endOf('month');
  const dataInizio = meseInizio;
  const dataFine = meseFine;
      const dataInizioStr = dataInizio.format('YYYY-MM-DD');
      const dataFineStr = dataFine.format('YYYY-MM-DD');
      
      const emptyGrid = generateEmptyMonthGrid(selectedDate);
      
      // Usa codice personale (oldId) invece del nome per filtrare i turni
      const oldId = userInfo.oldId || '';
      const rawData = await apiRequest<any[]>(`/orari-generati/codice/${encodeURIComponent(oldId)}?dataInizio=${dataInizioStr}&dataFine=${dataFineStr}`);

      if (rawData && rawData.length > 0) {
        const data: TurnoCreato[] = rawData.map(item => ({
          ...item,
          data: item.data,
          blocco: item.blocco || 0,
          preferenza: item.preferenza || 0
        }));
        const prefCount = data.filter(d => isPreferenceTurno(d) && d.id > 0 && dayjs(d.data).isSame(selectedDate, 'month')).length;
        setPreferenceCount(prefCount);
        
        // Costruisci una griglia che includa tutte le righe per ogni giorno
        const filledGrid: TurnoCreato[] = [];
        for (const emptyDay of emptyGrid) {
          const turniPerGiorno = data.filter(d => d.data === emptyDay.data);
          if (turniPerGiorno.length > 0) {
            for (const td of turniPerGiorno) {
              filledGrid.push({
                ...td,
                outOfMonth: (emptyDay as any).outOfMonth
              } as TurnoCreato);
            }
          } else {
            filledGrid.push(emptyDay);
          }
        }

        const ordered = sortOrariByDate(filledGrid);
        setOrari(ordered);
        // Statistiche calcolate dal backend via useStatsDipendente
      } else {
        setOrari(emptyGrid);
        setPreferenceCount(0);
        // Statistiche calcolate dal backend via useStatsDipendente
      }
    } catch (err: any) {
      console.error('Error fetching orari:', err);
      setError(err.message || 'Errore nel caricamento degli orari');
      const grid = generateEmptyMonthGrid(selectedDate);
      setOrari(grid);
      setPreferenceCount(0);
      // Statistiche calcolate dal backend via useStatsDipendente
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  // Carica statistiche cambi turno
  const fetchCambiTurnoStats = async () => {
    try {
      const stats = await cambiTurnoService.getStatistiche();
      setCambiTurnoStats(stats);
    } catch (err) {
      console.error('Error fetching cambi turno stats:', err);
    }
  };

  // Helper settimana: Lunedì-Domenica
  const getWeekRange = (dateStr: string) => {
    const d = dayjs(dateStr);
    const dow = d.day(); // 0=Dom,6=Sab
    const daysToMonday = (dow + 6) % 7;
    const monday = d.subtract(daysToMonday, 'day');
    const week: string[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(monday.add(i, 'day').format('YYYY-MM-DD'));
    }
    return week;
  };
  const isSameWeek = (a: string, b: string) => {
    const [am, bm] = [getWeekRange(a)[0], getWeekRange(b)[0]];
    return am === bm;
  };

  // Preferenze: operazioni con aggiornamento parziale (evita refresh pagina)
  const insertPreferencePartial = async (data: string, turno: string) => {
    try {
  const response = await preferenzeService.insertPreference(data, turno);
  if (response?.success === false) {
        throw new Error(response.message || 'Errore inserimento preferenza');
      }
      // Aggiornamento locale: usa l'ID reale restituito dal backend e eventuali orari previsti
	      const created = getPreferencePayload(response);
	      setOrari(prev => prev.map(o => (
	        o.data === data
	          ? ({
	              ...o,
	              id: created?.id ?? o.id,
	              blocco: 1,
	              preferenza: 1,
		              turno: created?.turno ?? turno,
	              oraPrevInizio: created?.oraPrevInizio ?? o.oraPrevInizio,
	              oraPrevFine: created?.oraPrevFine ?? o.oraPrevFine,
	              codiceTipoOrario: created?.codiceTipoOrario ?? (o as any).codiceTipoOrario
	            } as any)
          : o
      )));
	      // Aggiorna il conteggio se è nel mese visibile e non era già preferenza
	      setPreferenceCount(prevCount => {
	        const alreadyPref = orari.some(o => o.data === data && isPreferenceTurno(o));
	        return (dayjs(data).isSame(selectedDate, 'month') && !alreadyPref) ? prevCount + 1 : prevCount;
	      });
      setError(null);
    } catch (e: any) {
      // Gestione errori
      const errorMsg = e.message || e.response?.data?.message || '';
      console.error('✗ Errore inserimento preferenza:', errorMsg);
      setError(errorMsg || 'Errore preferenze');
      throw e;
  }
  };

  const updatePreferencePartial = async (turnoId: number, nuovoTurno: string) => {
    try {
      const response = await preferenzeService.updatePreference(turnoId, nuovoTurno);
      if (response?.success === false) {
        throw new Error(response.message || 'Errore modifica preferenza');
      }
      // Aggiornamento locale: usa id per trovare la riga
	      const updated = getPreferencePayload(response);
	      setOrari(prev => prev.map(o => (
	        o.id === turnoId
	          ? ({
	              ...o,
	              blocco: 1,
	              preferenza: 1,
	              turno: updated?.turno ?? nuovoTurno,
	              oraPrevInizio: updated?.oraPrevInizio ?? o.oraPrevInizio,
	              oraPrevFine: updated?.oraPrevFine ?? o.oraPrevFine,
	              codiceTipoOrario: updated?.codiceTipoOrario ?? o.codiceTipoOrario
	            } as any)
          : o
      )));
      setError(null);
    } catch (e: any) {
      console.error('updatePreferencePartial error:', e);
      setError(e.message || 'Errore preferenze');
      throw e;
  }
  };

  // Gestisce il click sul cuoricino per inserire/modificare preferenza
  // Fa un check al DB per verificare se esiste un record RIPOSO per la data
  const handleHeartClick = async (orario: TurnoCreato) => {
    if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) {
      alert(LOCK_MESSAGE);
      return;
    }
    if (isTurnoBlocked(orario)) {
      alert(`Modifiche bloccate fino al ${dataBloccante?.format('YYYY-MM-DD')}`);
      return;
    }
    
	    if (isPreferenceTurno(orario)) {
      setSelectedOrario(orario);
      setDialogType('modify');
      setTurnoDialogOpen(true);
      return;
    }
    
	    // Regola: max 2 preferenze per SETTIMANA
	    const weekPrefs = orari.filter(o => isPreferenceTurno(o) && isSameWeek(o.data, orario.data));
    if (weekPrefs.length >= 2) {
      setLimitDialogWeekPrefs(weekPrefs.map(w => ({ id: w.id, data: w.data, turno: w.turno })));
      setLimitDialogOpen(true);
      return;
    }
    
    // Se l'orario ha già un ID positivo, usa i dati locali
    let finalOrario = orario;
    let isExistingRiposoRecord = false;
    
    if (orario.id > 0) {
      // Record esistente - verifica se è RIPOSO (con o senza turno assegnato)
      isExistingRiposoRecord = isRiposoSenzaTurno(orario) || isRiposoDay(orario);
    } else {
      // ID negativo = riga vuota locale, verifico se esiste un record nel DB per questa data
      try {
	        const dataStr = orario.data;
	        const oldIdCheck = userInfo.oldId || '';
	        const negozioCheck = userInfo.negozio || userInfo.neg || '';
	        const negParamCheck = negozioCheck ? `&neg=${encodeURIComponent(negozioCheck)}` : '';
	        const checkData = await apiRequest<any[]>(`/orari-generati/codice/${encodeURIComponent(oldIdCheck)}?dataInizio=${dataStr}&dataFine=${dataStr}${negParamCheck}`);
        
        if (checkData && checkData.length > 0) {
          // Esiste un record nel DB per questa data
          const dbRecord = checkData[0];
          const isTurnoNullOrEmpty = !dbRecord.turno || dbRecord.turno.trim() === '';
          const isCodiceTipoOrarioRiposo = dbRecord.codiceTipoOrario === 'RIPOSO';
          const isTurnoRiposo = (dbRecord.turno || '').toLowerCase() === 'riposo';
          
          if ((isTurnoNullOrEmpty && isCodiceTipoOrarioRiposo) || (isTurnoRiposo && isCodiceTipoOrarioRiposo)) {
            // È un record RIPOSO (con o senza turno) - usa UPDATE
            isExistingRiposoRecord = true;
            // Aggiorna l'orario con i dati dal DB
            finalOrario = {
              ...orario,
              id: dbRecord.id,
              turno: dbRecord.turno,
              codiceTipoOrario: dbRecord.codiceTipoOrario,
              blocco: dbRecord.blocco
            };
          }
        }
      } catch (err) {
        // Nessun record esistente per questa data, procedo con INSERT
      }
    }
    
    setSelectedOrario(finalOrario);
    if (isExistingRiposoRecord) {
      setDialogType('updateRiposo');
    } else {
      setDialogType('insert');
    }
    setTurnoDialogOpen(true);
  };

  // Aggiorna un record esistente con turno NULL e codiceTipoOrario = RIPOSO per trasformarlo in preferenza
  const updateRiposoToPreferencePartial = async (turnoId: number, nuovoTurno: string) => {
    try {
      const response = await preferenzeService.updateRiposoToPreference(turnoId, nuovoTurno);
      if (response?.success === false) {
        throw new Error(response.message || 'Errore aggiornamento preferenza');
      }
	      const updated = getPreferencePayload(response);
      const giornoData = orari.find(o => o.id === turnoId)?.data;
	      setOrari(prev => prev.map(o => (
	        o.id === turnoId
	          ? ({
	              ...o,
	              blocco: 1,
	              preferenza: 1,
	              turno: updated?.turno ?? nuovoTurno,
	              oraPrevInizio: updated?.oraPrevInizio ?? o.oraPrevInizio,
	              oraPrevFine: updated?.oraPrevFine ?? o.oraPrevFine,
	              codiceTipoOrario: updated?.codiceTipoOrario ?? o.codiceTipoOrario
	            } as any)
          : o
      )));
      // Aggiorna il conteggio preferenze
      if (giornoData && dayjs(giornoData).isSame(selectedDate, 'month')) {
        setPreferenceCount(prevCount => prevCount + 1);
      }
      setError(null);
    } catch (e: any) {
      console.error('updateRiposoToPreferencePartial error:', e);
      setError(e.message || 'Errore preferenze');
      throw e;
    }
  };

  const deletePreferencePartial = async (turnoId: number) => {
    try {
      // Individua la riga attuale per ricostruire il placeholder
      const current = orari.find(o => o.id === turnoId);
      const giornoData = current?.data || null;
	      const wasPref = isPreferenceTurno(current);

      // 1) Cancella la preferenza lato backend (converte in turno baseline)
      const response = await preferenzeService.deletePreference(turnoId);
      if (response?.success === false) {
        throw new Error(response.message || 'Errore cancellazione preferenza');
      }

      // 2) Aggiornamento locale: aggiorna con il turno baseline restituito dal backend
      if (response && response.turno) {
        const turnoBaseline = response.turno;
        
        setOrari(prev => prev.map(o => {
          if (o.id === turnoId) {
            return {
              ...turnoBaseline,
	              id: turnoBaseline.id,
	              blocco: turnoBaseline.blocco === 1 ? 1 : 0,
	              preferenza: turnoBaseline.preferenza || 0,
	              outOfMonth: (o as any).outOfMonth,
              codiceTipoOrario: turnoBaseline.codiceTipoOrario || 'RIPOSO',
              turno: turnoBaseline.turno || 'Disponibile'
            } as any as TurnoCreato;
          }
          return o;
        }));
      }

      // 4) Aggiorna conteggio preferenze se necessario
      if (wasPref && giornoData && dayjs(giornoData).isSame(selectedDate, 'month')) {
        setPreferenceCount(c => Math.max(0, c - 1));
      }
      setError(null);
    } catch (e: any) {
      console.error('deletePreferencePartial error:', e);
      setError(e.message || 'Errore preferenze');
      throw e;
    }
  };

  // Effect per caricare i dati quando cambia la data
  useEffect(() => {
    fetchOrari();
    fetchCambiTurnoStats();
  }, [selectedDate]);

  // Nuovo effect per caricare informazioni dipendente
  useEffect(() => {
    const loadInfo = async () => {
      try {
        const all = await apiRequest<Dipendente[]>('/dipendenti');
        setDipInfo(all.find(d => d.nome === (userInfo.nome || '').trim()) || null);
      } catch (e) { setDipInfo(null); }
    };
    loadInfo();
  }, []);

  // Mappa mesi per API backend statistiche
  const mesiNomi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const meseNome = mesiNomi[selectedDate.month()];
  const annoStats = selectedDate.year();
  const nomeDipStats = (userInfo.nome || '').trim();

  // Usa API backend per statistiche (stessa logica di GenerazioneOrario)
  const { data: statsData, refetch: refetchStats } = useStatsDipendente(nomeDipStats, meseNome, annoStats);

  // Sincronizza statistiche dal backend allo stato locale
  useEffect(() => {
    if (statsData) {
      const s = statsData as any;
      setStats({
        oreContratto: s.oreContratto ?? 0,
        orePianificate: s.orePianificate ?? 0,
        oreConsuntivate: s.oreConsuntivate ?? 0,
        assenzeContrattuali: s.assenzeContrattuali ?? 0,
        attuale: s.attuale ?? 0,
        differenzaAttuale: s.differenzaAttuale ?? 0
      });
    }
  }, [statsData]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography>Caricamento orari...</Typography>
      </Container>
    );
  }

  const isLocked = isLockedForUser(selectedDate.year(), selectedDate.month() + 1);

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {isLocked && (<Alert severity="warning" sx={{ mb: 2 }}>{LOCK_MESSAGE}</Alert>)}
      {/* Header mobile */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Schedule fontSize="large" sx={{ mr: 1, color: PAGE_COLOR }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
            Il Mio Orario
          </Typography>
          {/* <Tooltip title="Gestione Cambi Turno">
            <IconButton 
              onClick={() => setCambiTurnoPopupOpen(true)}
              sx={{ ml: 1 }}
              color={cambiTurnoStats?.haRichiestePendenti ? 'warning' : 'default'}
            >
              {cambiTurnoStats?.haRichiestePendenti ? <ChangeCircle /> : <ChangeCircleOutlined />}
            </IconButton>
          </Tooltip> */}
        </Box>
        
        {/* Mese corrente e pulsante calendario */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {selectedDate.format('MMMM YYYY').charAt(0).toUpperCase() + selectedDate.format('MMMM YYYY').slice(1)}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<CalendarToday />}
            onClick={() => setShowMonthYearPicker(true)}
            size="small"
          >
            Cambia mese
          </Button>
        </Box>

        {/* Informazioni sui periodi preferenze */}
        {isPreferencesPeriod && isTargetMonth && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ textAlign: 'initial' }}>
              <strong>Inserimento preferenze attivo</strong>
              <br />
              Puoi inserire massimo 2 preferenze a settimana per <strong>{selectedDate.format('MMMM YYYY')}</strong>
              <br />
              <span style={{ color: '#1976d2' }}>
                Clicca sul cuore al fianco della data per inserire una preferenza
              </span>
            </Typography>
          </Alert>
        )}

        {isPreferencesPeriod && !isTargetMonth && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Puoi inserire preferenze solo per il mese di <strong>{calculateTargetMonth().format('MMMM YYYY')}</strong>.
              <br />
              Seleziona il mese indicato per inserire le preferenze.
            </Typography>
          </Alert>
        )}

        {!isPreferencesPeriod && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Le preferenze sono disponibili <strong>dal 16 alla fine del mese corrente </strong>
              per il mese di <strong>{dayjs().add(2, 'month').format('MMMM YYYY')}</strong>.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Calendario collassabile */}
      <Collapse in={showCalendar}>
        <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Seleziona mese/anno</Typography>
              <IconButton size="small" onClick={() => setShowCalendar(false)}>
                <Close />
              </IconButton>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
              <StaticDatePicker
                value={selectedDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedDate(newValue);
                    setShowCalendar(false);
                  }
                }}
                views={['year','month']}
                openTo="month"
                displayStaticWrapperAs="mobile"
                slotProps={{
                  actionBar: { actions: [] },
                }}
              />
            </LocalizationProvider>
          </CardContent>
        </Card>
      </Collapse>

      {/* Messaggi di errore */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Banner informativo: periodo bloccato fino alla data più restrittiva tra DATA_BLOCCO_GLOBALE e DataControllo */}
      {!!dataBloccante && (() => {
        // Verifica se il mese selezionato è successivo alla data di blocco
        const lastDayOfSelectedMonth = selectedDate.endOf('month');
        const isSelectedMonthAfterBlock = lastDayOfSelectedMonth.isAfter(dataBloccante, 'day');
        
        // Mostra l'alert solo se il mese selezionato non è successivo alla data di blocco
        if (isSelectedMonthAfterBlock) {
          return null;
        }
        
        return (
          <Alert severity={dataBloccoGlobale && (!dataControllo || dataBloccoGlobale.isAfter(dataControllo, 'day') || dataBloccoGlobale.isSame(dataControllo, 'day')) ? "error" : "info"} sx={{ mb: 2 }}>
            Modifiche bloccate fino al {dataBloccante.format('DD/MM/YYYY')}
          </Alert>
        );
      })()}

      {/* Lista delle carte per ogni giorno */}
      <Stack spacing={2}>
        {orari.map((orario) => {
          const isEditing = editingRows[orario.id];
          const showActions = showActionsForRow === orario.id;
          const isExpanded = expandedCards.has(orario.id);
          const isRealPreference = isPreferenceTurno(orario);
          const weekCount = orari.filter(o => isPreferenceTurno(o) && isSameWeek(o.data, orario.data)).length;
          const isBlocked = isTurnoBlocked(orario);
          // Cuore cliccabile anche quando si è raggiunto il limite settimanale, per mostrare il popup di gestione
          // I giorni di Riposo devono sempre permettere il cuoricino
          const heartDisabled = isBlocked || (isRealPreference ? false : (isRiposoDay(orario) ? !!orario.outOfMonth : (!!orario.oraPrevInizio || !!orario.outOfMonth)));
      const heartTitle = isRealPreference
            ? 'Modifica preferenza'
            : (isRiposoDay(orario)
                ? (weekCount >= 2 ? 'Hai già selezionato 2 preferenze questa settimana' : 'Aggiungi preferenza')
                : (!!orario.oraPrevInizio
                    ? 'Non selezionabile (ha orario previsto)'
                    : (weekCount >= 2 ? 'Hai già selezionato 2 preferenze questa settimana' : 'Aggiungi preferenza')));
          
          const storeNeg = getStoreCodeForTurno(orario);
          // Verifica se il dipendente può modificare questo turno in base al tipo orario
          const role = (userInfo.ruolo || '').toLowerCase();
          const isDipendente = role === 'dipendente';
          const isAdminOrCoord = role === 'admin' || role === 'coordinatore';
          // Il RIPOSO baseline vuoto resta non modificabile per il dipendente.
          const isRiposoVuoto = isRiposoSenzaTurno(orario);
          const tipoOrarioTurno = orario.codiceTipoOrario || null;
          const canModifyThisTurno = !isDipendente || canDipendenteModifyTipoOrario(tipoOrarioTurno, permessiSpeciali);
          
          // Verifica se ci sono turni LAVORATO precedenti non confermati (per ruoli con restrizione)
          const hasPreviousUnconfirmed = isRuoloConRestrizioneConferma(role) && hasPreviousUnconfirmedLavorato(orari, orario.data);
          // Blocca l'inserimento di OraConsInizio e OraConsFine se ci sono turni precedenti non confermati
          const canEditConsuntivo = !hasPreviousUnconfirmed;
          
          // Per la visualizzazione, nascondi il codiceTipoOrario se è un "RIPOSO senza turno"
          const displayCodiceTipoOrario = isRiposoVuoto ? null : orario.codiceTipoOrario;
          
          // Usa gli helper per determinare label e colore del turno
          const turnoLabel = getTurnoDisplayLabel(orario);
          const turnoColor = getTurnoForColor(orario);
          
	          return (
		            <Card id={`card-${orario.id}`} key={`${orario.id}-${orario.data}`} sx={{
		              borderLeft: `4px solid ${getTurnoDisplayColor(turnoColor)}`,
	              // Solo dipendenti e coordinatori: blocco completo (non cliccabile)
		              ...(!isAdminOrCoord && isBlocked && {
		                backgroundColor: '#bdbdbd',
		                opacity: 0.9,
		                pointerEvents: 'none'
		              }),
		              // Coordinatori: visualizzazione grigia ma cliccabile (Admin non è mai bloccato)
		              ...(role === 'coordinatore' && isBlocked && {
		                backgroundColor: '#bdbdbd',
		                opacity: 0.9
	              }),
	              ...(isPreferenceTurno(orario) && !isBlocked && {
	                backgroundColor: 'rgba(255, 182, 193, 0.1)',
	                borderColor: '#ff69b4',
	                opacity: 1,
	                pointerEvents: 'auto'
	              }),
		              boxShadow: 2
	            }}>
              <CardContent sx={{ py: 2 }}>
                {/* Header della card */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {formatDataDisplay(orario.data)}
                    </Typography>
                    {/* Griglia 2 colonne per allineare il chip Negozio con l'inizio di "Orario effettivo" */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', columnGap: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip 
                          label={turnoLabel} 
                          size="small" 
                          sx={{ 
                            backgroundColor: getTurnoDisplayColor(turnoColor),
                            color: 'white',
                            fontWeight: 'bold'
                          }} 
                        />
                      </Box>
                      {storeNeg ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Chip 
                            label={<span translate="no">{storeNeg}</span>}
                            size="small"
                            sx={{ 
                              backgroundColor: getTurnoDisplayColor(turnoColor),
                              color: '#fff',
                              fontWeight: 700,
                              height: 22 
                            }}
                          />
                        </Box>
                      ) : <Box />}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(((isPreferencesPeriod && isTargetMonth && !orario.outOfMonth)) || isRealPreference) && (
                      <Tooltip title={heartTitle}>
                        <span>
                          <IconButton
                            size="small"
                            color={isRealPreference ? 'error' : 'default'}
                            disabled={heartDisabled}
                            onClick={() => handleHeartClick(orario)}
                          >
                            {isRealPreference ? <Favorite /> : <FavoriteBorder />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <IconButton 
                      size="small"
                      onClick={() => toggleCardExpansion(orario.id)}
                    >
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </Box>

                {/* Informazioni base sempre visibili - nascondi orari per riposo (solo se è un vero riposo con turno assegnato) */}
                {!isRiposoSenzaTurno(orario) && orario.turno !== 'Riposo' && orario.codiceTipoOrario !== 'CHIUSO DITTA' && (
                  // Usa griglia 2 colonne per allineare le etichette con i chip sopra
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Orario previsto
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatTimeRange(orario.oraPrevInizio, orario.oraPrevFine)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Orario effettivo
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: (isAssenzaTipo(orario.codiceTipoOrario) && !isAssenzaConConsuntivo(orario.codiceTipoOrario))
                            ? 'inherit' 
                            : (orario.oraConsInizio && orario.oraConsFine) ? 'green' : 'orange' 
                        }}
                      >
                        {(isAssenzaTipo(orario.codiceTipoOrario) && !isAssenzaConConsuntivo(orario.codiceTipoOrario))
                          ? '-' 
                          : formatTimeRange(orario.oraConsInizio, orario.oraConsFine)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Slider swipe-to-copy: sempre se abilitato da Impostazioni; altrimenti solo per oggi o futuro */}
                {(() => {
                  // Nascondi slider in periodo bloccato
                  if (isLockedForUser(selectedDate.year(), selectedDate.month() + 1)) return false;
                  // Nascondi slider se la data è bloccata
                  if (isBlocked) return false;
                  // NON nascondere più se ci sono turni precedenti non confermati - il check avverrà al momento dello swipe
                  // Nascondi slider se CHIUSO DITTA
                  if (orario.codiceTipoOrario === 'CHIUSO DITTA') return false;
                  // Nascondi slider per turni ASSENZA - tranne per assenze che permettono consuntivo (ROL, LEGGE 104, ecc.)
                  if (isAssenzaTipo(orario.codiceTipoOrario) && !isAssenzaConConsuntivo(orario.codiceTipoOrario)) return false;
                  // Deve avere orari previsti
                  if (!orario.oraPrevInizio || !orario.oraPrevFine) return false;
                  // Nascondi slider se orari previsti sono entrambi 00:00 (non ha senso consolidare)
                  if (orario.oraPrevInizio?.substring(0, 5) === '00:00' && orario.oraPrevFine?.substring(0, 5) === '00:00') return false;
                  
                  // Mostra slider SOLO se ENTRAMBI oraConsInizio e oraConsFine sono vuoti
                  if (orario.oraConsInizio || orario.oraConsFine) return false;
                  
                  // Non deve essere in editing
                  if (isEditing) return false;
                  
                  // Se slider sempre abilitato, mostra sempre
                  if (sliderAlwaysEnabled) return true;
                  
                  // Altrimenti solo per giorni con data <= oggi
                  const today = dayjs();
                  const orarioDate = dayjs(orario.data);
                  return orarioDate.isBefore(today, 'day') || orarioDate.isSame(today, 'day');
                })() && (
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <SwipeToCopySlider
                      onComplete={() => {
                        // Se ci sono turni precedenti non confermati, mostra warning invece di confermare
                        if (hasPreviousUnconfirmed) {
                          alert('Devi prima consolidare i turni precedenti del mese (LAVORATO, ROL, AVIS, ecc.)');
                          return;
                        }
                        copyPrevToConsuntivo(orario.id, orario.oraPrevInizio, orario.oraPrevFine);
                      }}
                      baseColor={getTurnoDisplayColor(turnoColor)}
                    />
                  </Box>
                )}

                {/* Dettagli espandibili */}
                <Collapse in={isExpanded}>
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Campi di modifica */}
                  <Stack spacing={2}>
                    {/* Orari - Nascosti se CHIUSO DITTA (ma mostrati per RIPOSO senza turno) */}
                    {(displayCodiceTipoOrario !== 'CHIUSO DITTA') && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Ora inizio
                        </Typography>
                        {isEditing ? (
                          <TextField
                            type="time"
                            value={isEditing.oraConsInizio}
                            onChange={(e) => updateEditingValue(orario.id, 'oraConsInizio', e.target.value)}
                            size="small"
                            fullWidth
                            disabled={!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(isEditing.tipoOrario)}
                            sx={{ 
                              mt: 0.5,
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: '#666',
                                backgroundColor: '#f0f0f0'
                              }
                            }}
                          />
                        ) : (
                          <Tooltip title={hasPreviousUnconfirmed ? 'Devi prima consolidare i turni precedenti del mese (LAVORATO, ROL, AVIS, ecc.)' : ''}>
                          <Box
                            onClick={() => {
                              if (canModifyThisTurno && canEditConsuntivo && !isDisableConsuntivo(displayCodiceTipoOrario)) {
                                handleCellClick(orario.id, 'oraConsInizio', orario.oraConsInizio);
                              }
                            }}
                            sx={{
                              cursor: (!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(displayCodiceTipoOrario)) ? 'not-allowed' : 'pointer',
                              p: 1.5,
                              border: '1px solid #ddd',
                              borderRadius: 1,
                              backgroundColor: (!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(displayCodiceTipoOrario)) ? '#f0f0f0' : '#f9f9f9',
                              color: (!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(displayCodiceTipoOrario)) ? '#666' : 'inherit',
                              mt: 0.5,
                              minHeight: '40px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Typography variant="body2">
                              {isDisableConsuntivo(displayCodiceTipoOrario)
                                ? '—'
                                : (orario.oraConsInizio ? formatTime(orario.oraConsInizio) : (canModifyThisTurno && canEditConsuntivo ? 'Tocca per inserire' : (hasPreviousUnconfirmed ? 'Consolida turni precedenti' : 'Non modificabile')))}
                            </Typography>
                          </Box>
                          </Tooltip>
                        )}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Ora fine
                        </Typography>
                        {isEditing ? (
                          <TextField
                            type="time"
                            value={isEditing.oraConsFine}
                            onChange={(e) => updateEditingValue(orario.id, 'oraConsFine', e.target.value)}
                            size="small"
                            fullWidth
                            disabled={!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(isEditing.tipoOrario)}
                            sx={{ 
                              mt: 0.5,
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: '#666',
                                backgroundColor: '#f0f0f0'
                              }
                            }}
                          />
                        ) : (
                          <Tooltip title={hasPreviousUnconfirmed ? 'Devi prima consolidare i turni precedenti del mese (LAVORATO, ROL, AVIS, ecc.)' : ''}>
                          <Box
                            onClick={() => {
                              if (canModifyThisTurno && canEditConsuntivo && !isDisableConsuntivo(displayCodiceTipoOrario)) {
                                handleCellClick(orario.id, 'oraConsFine', orario.oraConsFine);
                              }
                            }}
                            sx={{
                              cursor: (!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(displayCodiceTipoOrario)) ? 'not-allowed' : 'pointer',
                              p: 1.5,
                              border: '1px solid #ddd',
                              borderRadius: 1,
                              backgroundColor: (!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(displayCodiceTipoOrario)) ? '#f0f0f0' : '#f9f9f9',
                              color: (!canModifyThisTurno || !canEditConsuntivo || isDisableConsuntivo(displayCodiceTipoOrario)) ? '#666' : 'inherit',
                              mt: 0.5,
                              minHeight: '40px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Typography variant="body2">
                              {isDisableConsuntivo(displayCodiceTipoOrario)
                                ? '—'
                                : (orario.oraConsFine ? formatTime(orario.oraConsFine) : (canModifyThisTurno && canEditConsuntivo ? 'Tocca per inserire' : (hasPreviousUnconfirmed ? 'Consolida turni precedenti' : 'Non modificabile')))}
                            </Typography>
                          </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    )}

                    {/* Tipo orario */}
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Tipo orario
                      </Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                          <Select
                            value={isEditing.tipoOrario}
                            onChange={(e) => updateEditingValue(orario.id, 'tipoOrario', e.target.value)}
                            open={openTipoOrarioId === orario.id}
                            onClose={() => setOpenTipoOrarioId(null)}
                            onOpen={() => setOpenTipoOrarioId(orario.id)}
                            disabled={!canModifyThisTurno}
                          >
                            <MenuItem value="">Seleziona...</MenuItem>
                            {(() => {
                              const current = isEditing.tipoOrario;
                              // Base ristrette
                              let baseRestricted = [...TIPO_ORARIO_OPTIONS_RESTRICTED];
                              // Abilita LEGGE 104 solo se il backend segnala permesso speciale (Tipo=1) e ci sono giorni rimanenti
                              if (permessiSpeciali && permessiSpeciali.hasSpecial && permessiSpeciali.tipo === 1 && (permessiSpeciali.remainingGg ?? 0) > 0) {
                                if (!baseRestricted.includes('LEGGE 104 PERMESSO')) baseRestricted.push('LEGGE 104 PERMESSO');
                              }
                              let options = baseRestricted.includes(current)
                                ? baseRestricted
                                : (current ? [current, ...baseRestricted] : baseRestricted);
                              
                              // FILTRO ROL: Se ore contrattuali = 0, nascondi ROL
                              if (dipInfo) {
                                const d = dayjs(orario.data);
                                const dayOfWeek = d.day(); // 0=Dom, 1=Lun...
                                let hours = 0;
                                switch (dayOfWeek) {
                                  case 1: hours = dipInfo.lunedi; break;
                                  case 2: hours = dipInfo.martedi; break;
                                  case 3: hours = dipInfo.mercoledi; break;
                                  case 4: hours = dipInfo.giovedi; break;
                                  case 5: hours = dipInfo.venerdi; break;
                                  case 6: hours = dipInfo.sabato; break;
                                  case 0: hours = dipInfo.domenica; break;
                                }
                                if (hours <= 0) {
                                  options = options.filter(o => o !== 'ROL');
                                }
                              }

                              // Remove duplicates
                              let uniqueOptions = Array.from(new Set(options));

                              // FILTRO CHIUSO DITTA: Mostra solo se la data è presente in festivita
                              uniqueOptions = uniqueOptions.filter(opt => {
                                if (opt === 'CHIUSO DITTA') {
                                  return festivitaData?.some((f: any) => f.dataFestivita === orario.data);
                                }
                                return true;
                              });

                              return uniqueOptions.map((tipo) => (
                                <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                              ));
                            })()}
                          </Select>
                        </FormControl>
                      ) : (
                        <Box 
                          onClick={() => canModifyThisTurno && handleCellClick(orario.id, 'tipoOrario', displayCodiceTipoOrario || '')}
                          sx={{ 
                            cursor: canModifyThisTurno ? 'pointer' : 'not-allowed',
                            p: 1.5,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            backgroundColor: canModifyThisTurno ? '#f9f9f9' : '#f0f0f0',
                            color: canModifyThisTurno ? 'inherit' : '#666',
                            mt: 0.5,
                            minHeight: '40px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Typography variant="body2">
                            {displayCodiceTipoOrario || (canModifyThisTurno ? 'Tocca per selezionare' : 'Non modificabile')}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Note */}
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Note
                      </Typography>
                      {isEditing ? (
                        <TextField
                          value={isEditing.annotazioni}
                          onChange={(e) => updateEditingValue(orario.id, 'annotazioni', e.target.value)}
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          disabled={!canModifyThisTurno}
                          sx={{ mt: 0.5 }}
                        />
                      ) : (
                        <Box 
                          onClick={() => canModifyThisTurno && handleCellClick(orario.id, 'annotazioni', orario.annotazioni || '')}
                          sx={{ 
                            cursor: canModifyThisTurno ? 'pointer' : 'not-allowed',
                            p: 1.5,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            backgroundColor: canModifyThisTurno ? '#f9f9f9' : '#f0f0f0',
                            color: canModifyThisTurno ? 'inherit' : '#666',
                            mt: 0.5,
                            minHeight: '60px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Typography variant="body2">
                            {(orario.annotazioni && orario.annotazioni !== '__RIGA_DUPLICATA__') ? 
                              orario.annotazioni : 'Tocca per aggiungere note'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Totale ore */}
                    {orario.turno !== 'Riposo' && (orario.oraConsInizio || orario.oraConsFine) && (
                      <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Totale ore: {calculateTotalHours(orario.oraConsInizio, orario.oraConsFine)}
                        </Typography>
                      </Box>
                    )}

                    {/* Azioni */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
                      {showActions ? (
                        <>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<Save />}
                              onClick={() => saveConsuntivo(orario.id)}
                              disabled={!canModifyThisTurno || isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              sx={{ flex: 1 }}
                            >
                              Salva
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => cancelEditing(orario.id)}
                              disabled={isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              sx={{ flex: 1 }}
                            >
                              Annulla
                            </Button>
                          </Box>
                          {orario.annotazioni !== '__RIGA_DUPLICATA__' && (
                            <Button
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={(e) => duplicateRow(e, orario)}
                              disabled={!canModifyThisTurno || isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              fullWidth
                            >
                              {canModifyThisTurno ? 'Duplica' : 'Non modificabile'}
                            </Button>
                          )}
                          {/* Bottone Cancella Riga: visibile solo per righe duplicate */}
                          {orario.annotazioni === '__RIGA_DUPLICATA__' && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<Remove />}
                              onClick={() => deleteRow(orario)}
                              disabled={!canModifyThisTurno || isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              fullWidth
                            >
                              {canModifyThisTurno ? 'Cancella Riga' : 'Non eliminabile'}
                            </Button>
                          )}
                          {/* Bottone Cancella: visibile se ore cons inizio/fine sono compilate e il tipo orario è modificabile dal dipendente */}
                          {orario.oraConsInizio && orario.oraConsFine && canModifyThisTurno && (
                            <Button
                              variant="outlined"
                              color="warning"
                              startIcon={<Delete />}
                              onClick={() => clearConsuntivoAndResetToLavorato(orario.id)}
                              disabled={isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              fullWidth
                            >
                              Cancella
                            </Button>
                          )}
                        </>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {orario.annotazioni !== '__RIGA_DUPLICATA__' && (
                            <Button
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={(e) => duplicateRow(e, orario)}
                              disabled={!canModifyThisTurno || isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              sx={{ flex: 1 }}
                            >
                              {canModifyThisTurno ? 'Duplica' : 'Non modificabile'}
                            </Button>
                          )}
                          {orario.annotazioni === '__RIGA_DUPLICATA__' && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<Remove />}
                              onClick={() => deleteRow(orario)}
                              disabled={!canModifyThisTurno || isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              sx={{ flex: 1 }}
                            >
                              {canModifyThisTurno ? 'Cancella Riga' : 'Non eliminabile'}
                            </Button>
                          )}
                          {(orario.oraConsInizio || orario.oraConsFine) && (
                            <Button
                              variant="outlined"
                              color="warning"
                              startIcon={<Cancel />}
                              onClick={() => handleCancelOrClear(orario.id, orario)}
                              disabled={!canModifyThisTurno || isLockedForUser(selectedDate.year(), selectedDate.month() + 1)}
                              sx={{ flex: 1 }}
                            >
                              Cancella
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
        
        {orari.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              Nessun orario trovato per il periodo selezionato
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Statistiche mensili */}
      <Card sx={{ mt: 3, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Statistiche Mensili
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Ore Contratto
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatHoursNumberToHHSS(stats.oreContratto)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Ore Pianificate
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatHoursNumberToHHSS(stats.orePianificate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Ore Consuntivate
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatHoursNumberToHHSS(stats.oreConsuntivate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Assenze Contrattuali
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatHoursNumberToHHSS(stats.assenzeContrattuali)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Attuale
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: stats.attuale >= stats.oreContratto ? 'green' : 'red' }}>
                {formatHoursNumberToHHSS(stats.attuale)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Differenza Attuale
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: stats.differenzaAttuale >= 0 ? 'green' : 'red' }}>
                {formatHoursNumberToHHSS(stats.differenzaAttuale)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Contratto: Tipo Contratto + ore settimanali (come in MioOrario) */}
      <Card sx={{ mt: 2, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Contratto
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.5, columnGap: 1 }}>
            <Typography variant="caption" color="text.secondary">Tipo Contratto</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {(() => {
                const df = dipInfo?.dataFineContratto?.slice(0, 10);
                // Considera INDETERMINATO anche quando la data fine non è valorizzata
                return !df || df === '2099-12-31' ? 'INDETERMINATO' : 'DETERMINATO';
              })()}
            </Typography>

            {/* Giornaliero */}
            <Typography variant="caption" color="text.secondary">Lun</Typography>
            <Typography variant="body2">{formatHoursNumberToHHSS(dipInfo?.lunedi || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">Mar</Typography>
            <Typography variant="body2">{formatHoursNumberToHHSS(dipInfo?.martedi || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">Mer</Typography>
            <Typography variant="body2">{formatHoursNumberToHHSS(dipInfo?.mercoledi || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">Gio</Typography>
            <Typography variant="body2">{formatHoursNumberToHHSS(dipInfo?.giovedi || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">Ven</Typography>
            <Typography variant="body2">{formatHoursNumberToHHSS(dipInfo?.venerdi || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">Sab</Typography>
            <Typography variant="body2">{formatHoursNumberToHHSS(dipInfo?.sabato || 0)}</Typography>
            <Typography variant="caption" color="text.secondary">Dom</Typography>
            <Typography variant="body2">{formatHoursNumberToHHSS(dipInfo?.domenica || 0)}</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* FAB per espandere/comprimere tutte le card */}
      <Fab
        color="primary"
        sx={{ 
          position: 'fixed', 
          bottom: 16, 
          right: 16,
          zIndex: 1000
        }}
        onClick={() => {
          if (expandedCards.size === 0) {
            // Espandi tutte
            setExpandedCards(new Set(orari.map(o => o.id)));
          } else {
            // Comprimi tutte
            setExpandedCards(new Set());
          }
        }}
      >
        {expandedCards.size === 0 ? <ExpandMore /> : <ExpandLess />}
      </Fab>

      {/* Popup cambi turno */}
      {cambiTurnoPopupOpen && (
        <CambiTurnoPopup
          open={cambiTurnoPopupOpen}
          onClose={() => setCambiTurnoPopupOpen(false)}
          turniDipendente={orari}
          user={{
            nome: userInfo.nome || '',
            ruolo: userInfo.ruolo || 'Dipendente',
            negozio: userInfo.neg
          }}
          onRefresh={() => {
            fetchOrari();
            fetchCambiTurnoStats();
          }}
        />
      )}

      {/* Dialog preferenze */}
      <Dialog open={turnoDialogOpen} onClose={() => { if (!preferenceSubmitting) setTurnoDialogOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>
          {(dialogType === 'insert' || dialogType === 'updateRiposo') && 'Seleziona tipo di turno per la preferenza'}
          {dialogType === 'modify' && `Modifica preferenza: ${selectedOrario?.turno || ''}`}
        </DialogTitle>
        <DialogContent>
          {(() => {
            const isW = selectedOrario ? (dayjs(selectedOrario.data).day() === 0 || dayjs(selectedOrario.data).day() === 6) : false;
            // Intermezzo commentato per ora - non più utilizzato
            // const options = ['Mattina', 'Pomeriggio', 'Intermezzo', 'Riposo']
            const options = ['Mattina', 'Pomeriggio', 'Riposo']
              .filter(t => {
                 if (selectedOrario) {
                    // Se è weekend (sabato/domenica), nascondi "Riposo" - mostro solo Mattina e Pomeriggio
                    if (isW && t === 'Riposo') return false;
                 }
                 return true;
              });
            return (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                  {options.map((turno) => (
                    <Button
                      key={turno}
                      variant="contained"
                      fullWidth
	                      onClick={async () => {
	                        if (!selectedOrario || preferenceSubmitting) return;
	                        setPreferenceSubmitting(true);
	                        try {
	                          if (dialogType === 'insert') {
	                            await insertPreferencePartial(selectedOrario.data, turno);
	                          } else if (dialogType === 'updateRiposo') {
	                            // Aggiorna un record esistente con turno NULL e codiceTipoOrario = RIPOSO
	                            await updateRiposoToPreferencePartial(selectedOrario.id, turno);
	                          } else {
	                            await updatePreferencePartial(selectedOrario.id, turno);
	                          }
	                          setTurnoDialogOpen(false);
	                          setSelectedOrario(null);
	                          setError(null);
	                          setPreferenceSubmitting(false);
	                          fetchOrari({ silent: true }).catch(console.error);
	                        } catch (e: any) {
	                          setError(e.message || 'Errore preferenze');
	                          setPreferenceSubmitting(false);
	                        }
	                      }}
                      disabled={preferenceSubmitting}
                      sx={{
                        // Riposo centrato sotto Mattina e Pomeriggio nei giorni feriali (lun-ven)
                        ...(!isW && turno === 'Riposo' ? { gridColumn: '1 / -1', maxWidth: '50%', justifySelf: 'center' } : {}),
                        backgroundColor: getTurnoColor(turno),
                        color: getTurnoTextColor(turno),
                        borderRadius: 3,
                        py: 1.5,
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: getTurnoColor(turno), opacity: 0.85 }
                      }}
                    >
                      {turno}
                    </Button>
                  ))}
                </Box>
                {dialogType === 'modify' && isPreferenceTurno(selectedOrario) && (
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={async () => {
                        if (!selectedOrario || preferenceSubmitting) return;
                        const turnoId = selectedOrario.id;
	                        setPreferenceSubmitting(true);
	                        setTurnoDialogOpen(false);
	                        setSelectedOrario(null);
	                        try {
	                          // Cancella solo il giorno selezionato
	                          await deletePreferencePartial(turnoId);
	                          setError(null);
	                          setPreferenceSubmitting(false);
	                          fetchOrari({ silent: true }).catch(console.error);
	                        } catch (e: any) {
	                          setError(e.message || 'Errore cancellazione preferenza');
	                          setPreferenceSubmitting(false);
	                        }
	                      }}
                      disabled={preferenceSubmitting}
                      sx={{ borderRadius: 3 }}
                    >
                      Cancella Preferenza
                    </Button>
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTurnoDialogOpen(false)} disabled={preferenceSubmitting}>Annulla</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog limite preferenze settimanali */}
  <PreferenceLimitDialog
        open={limitDialogOpen}
        preferences={limitDialogWeekPrefs}
	        onDelete={async (id) => {
	          try {
	            await deletePreferencePartial(id);
	            await fetchOrari({ silent: true });
	            const remaining = limitDialogWeekPrefs.filter(p => p.id !== id);
	            setLimitDialogWeekPrefs(remaining);
	            if (remaining.length <= 1) setLimitDialogOpen(false);
	          } catch (e) {
            console.error('Errore cancellazione preferenza (mobile):', e);
          }
        }}
        onClose={() => setLimitDialogOpen(false)}
      />

      {/* Month Year Picker */}
      <MonthYearPicker
        open={showMonthYearPicker}
        onClose={() => setShowMonthYearPicker(false)}
        onConfirm={(month, year) => {
          setSelectedDate(dayjs().year(year).month(month));
          setShowMonthYearPicker(false);
        }}
        initialMonth={selectedDate.month()}
        initialYear={selectedDate.year()}
      />
    </Container>
  );
};

// Slider swipe-to-copy component (inline for simplicity)
const SwipeToCopySlider: React.FC<{
  onComplete: () => void;
  disabled?: boolean;
  // Colore base del turno (uguale al chip): es. #35abec, #60b964, #FDD65C, #d5d3d3
  baseColor?: string;
}> = ({ onComplete, disabled, baseColor = '#1976d2' }) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const knobRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState(0); // px
  const startXRef = useRef<number>(0);

  const thresholdPct = 0.8; // 80%

  // Helpers per usare il colore del chip con alpha
  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const h = hex.replace('#', '');
    if (h.length === 3) {
      const r = parseInt(h[0] + h[0], 16);
      const g = parseInt(h[1] + h[1], 16);
      const b = parseInt(h[2] + h[2], 16);
      return { r, g, b };
    }
    if (h.length === 6) {
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return { r, g, b };
    }
    return null;
  };
  const toRgba = (hex: string, a: number) => {
    const rgb = hexToRgb(hex);
    return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})` : `rgba(25,118,210,${a})`;
  };

  const endDrag = useCallback(() => {
    if (!trackRef.current) return;
    const trackWidth = trackRef.current.clientWidth;
    const completed = offset >= trackWidth * thresholdPct;
    if (completed && !disabled) {
      onComplete();
      // reset after a short delay so user sees completion
      setTimeout(() => setOffset(0), 150);
    } else {
      setOffset(0);
    }
    setDragging(false);
  }, [offset, onComplete, disabled]);

  useEffect(() => {
    const onMove = (clientX: number) => {
      if (!dragging || !trackRef.current) return;
      const rect = trackRef.current.getBoundingClientRect();
      let dx = clientX - startXRef.current;
      // limit between 0 and track width - knob width ~48
      const max = rect.width - 48;
      dx = Math.max(0, Math.min(dx, max));
      setOffset(dx);
    };

    const onMouseMove = (e: MouseEvent) => onMove(e.clientX);
    const onTouchMove = (e: TouchEvent) => { e.preventDefault(); onMove(e.touches[0].clientX); };

    const onUp = () => endDrag();

    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchend', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove as any);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, endDrag]);

  const startDrag = (clientX: number) => {
    if (disabled) return;
    startXRef.current = clientX;
    setDragging(true);
  };

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    startDrag(e.clientX);
  };
  const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    startDrag(e.touches[0].clientX);
  };

  return (
    <div ref={trackRef} style={{
      position: 'relative',
      height: 44,
      borderRadius: 22,
      background: disabled ? '#eee' : toRgba(baseColor, 0.12),
      overflow: 'hidden',
      userSelect: 'none',
      touchAction: 'none'
    }}>
      {/* Fill progress */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: Math.max(48, offset + 48),
        background: disabled ? '#ddd' : toRgba(baseColor, 0.35),
        transition: dragging ? 'none' : 'width 150ms ease',
        borderRadius: 22
      }} />
      {/* Text */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // Colore testo forzato a nero (richiesta): indipendente da stato disabled/baseColor
        color: '#000', fontWeight: 600, letterSpacing: 0.2
      }}>
        Conferma orario previsto
      </div>
      {/* Knob */}
      <div
        ref={knobRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          position: 'absolute', top: 2, left: 2 + offset,
          width: 40, height: 40, borderRadius: 20,
          background: disabled ? '#ccc' : baseColor,
          boxShadow: `0 2px 6px rgba(0,0,0,0.2), 0 0 0 6px ${toRgba(baseColor, 0.12)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff',
          transition: dragging ? 'none' : 'left 150ms ease',
          touchAction: 'none', cursor: disabled ? 'not-allowed' : 'grab',
          border: '2px solid #ffffff'
        }}
        aria-label="Trascina per copiare"
      >
        <ArrowForward sx={{ fontSize: 20 }} />
      </div>
    </div>
  );
};

export default MioOrarioCel;
